import React, { useState, useEffect, useCallback, useMemo, useRef, type DragEvent } from'react'
import { useNavigate, useLocation } from'react-router-dom'
import { 
 Calendar, Clock, Check, X, ChevronLeft, ChevronRight, 
 Sparkles, AlertTriangle, Target, Zap, RefreshCw, 
 GripVertical, CheckCircle2, ArrowRight, Loader2,
 CalendarDays, Repeat, RotateCw,
 Search, FileText,
 Lock, Unlock, Globe, Brain, Shield
} from'lucide-react'
import { Button } from'../components/ui/Button'
import { Card } from'../components/ui/Card'
import { format, addDays, startOfDay, parseISO, isSameDay, subDays } from'date-fns'
import { useServerTime } from'../hooks/useServerTime'
import { buildScheduledItemKeys } from './reviewSchedule/buildScheduledItemKeys'
import {
 canSetItemCommitment,
 confirmHolds as confirmHoldsScoped,
 createFlexibleBlocks,
 holdRowIdOf,
 holdSourceOf,
 mayRenderAsApplied,
 moveBlock,
 partitionForConfirm,
 releaseEndsHold,
 removeBlockFromDay,
 setItemCommitment,
} from'../services/aiSchedulerService'
// One definition of the Flexible/Confirmed vocabulary for the whole
// frontend (ratified decision #2) — this page does not get its own.
import { COMMITMENT_LABEL } from'../components/schedule/commitmentPresentation'
import type { CommitmentSource, CommitmentState } from'../services/scheduleService'
import { BRAND } from '../config/brand'

interface RecurrenceInfo {
 pattern:'daily' |'weekdays' |'weekly' |'biweekly' |'monthly' |'custom' | null
 days_of_week?: number[]
 preferred_time?: string
 end_date?: string
 rationale?: string
}

interface ScheduledItem {
 item_id: number
 item_type:'task' |'action_item' | 'habit' | 'skill' | 'roadblock'
 title: string
 suggested_date: string
 suggested_time: string
 duration_minutes: number
 rationale: string
 priority_score: number
 grouped_with: number[]
 accepted?: boolean
 modified?: boolean
 is_recurring?: boolean
 recurrence?: RecurrenceInfo | null
 // Block status:'confirmed' = locked (🔒),'proposed' = hold/unlocked (🔓)
 block_status?:'confirmed' |'proposed'
}

// Ratified decision #2: users read Flexible 🔓 / Confirmed 🔒. The
// backend keeps HOLD / CONFIRMED and this maps the row's stored
// `block_status` onto the two words the UI is allowed to say (SC-36).
const commitmentOfBlockStatus = (
 blockStatus?:'confirmed' |'proposed'
): CommitmentState => (blockStatus ==='proposed' ?'flexible' :'confirmed')

interface ScheduleSuggestion {
 scheduled_items: ScheduledItem[]
 optimization_notes: string
 warnings: string[]
 unscheduled_items: { item_id: number; reason: string }[]
}

interface FreeSlot {
 date: string
 start: string
 end: string
 duration_minutes: number
 day_of_week: string
}

interface CalendarEvent {
 id?: string | number
 title: string
 date: string
 start?: string
 end?: string
 all_day?: boolean
 source:'google_calendar' |'horton'
 block_type?: string
 block_status?: string
 block_source?: string
 // Phase 4: task_kind lets us separate habit-occurrence blocks from
 // task-like blocks in counters without branching on parent type.
 task_kind?: string
 external_system?: string
 parent_task_id?: number | string
 // Needed to correctly exclude already-scheduled habits from the
 // "To Schedule" list. Habit occurrence blocks frequently carry only
 // source_item_id / recurring_source_id (parent_task_id is often null),
 // so we thread all three through the mapper and join them into a
 // typed key (`task:42`, `habit:99`) downstream.
 source_item_id?: number | string
 source_item_type?: string
 recurring_source_id?: number | string
}

// Where a hold physically lives, which decides which verb can reach it.
//   'orchestrator_plan' — computed by plan-horizon, never persisted; it
//                         exists only in this session's state and is
//                         materialized by /api/orchestrator/confirm-holds.
//   'schedule_store'    — a real row (block_type='hold'), reachable by
//                         item id via toggle-lock, and by row id (when we
//                         have one) for move / remove.
type HoldOrigin = 'orchestrator_plan' | 'schedule_store'

// Orchestrator Hold Block
interface HoldBlock {
 id: string
 task_id: string
 // The hold's source item type. `task_id` alone collides across types
 // (task 7 and habit 7 are different things), so every membership test
 // joins the pair into a typed key.
 item_type: ItemToSchedule['type']
 title: string
 start: string // ISO datetime
 end: string // ISO datetime
 date_key: string // YYYY-MM-DD
 block_type:'hold'
 status:'proposed' |'confirmed'
 source:'ai_assist' |'horton_auto' |'horton_rebalanced' |'user'
 duration_minutes: number
 user_modified?: boolean
 origin: HoldOrigin
 // §2 dim-4 pair. Holds are FLEXIBLE/AI_PROPOSAL by construction; the
 // fields are carried explicitly so the chip renders the stored truth
 // rather than re-deriving it from block_status on every surface.
 commitment: CommitmentState
 commitment_source: CommitmentSource
 // The schedule row's own id, when this client knows it. Null for a
 // virtual occurrence expanded from a recurring master (its id is the
 // synthetic `<master>_v_<date>`), which is why per-block move/remove are
 // offered only where an id exists.
 block_id?: number | null
 // Whether `block_id` names one dated block or a recurring SERIES row.
 // The server's confirm matches a master by id, so confirming this
 // address commits every day the rule produces; its skip releases exactly
 // one of those days and leaves the rule running. Both verbs therefore
 // have to say which one they are doing (C7 / C12) instead of looking
 // like a decision about this square on the calendar.
 is_recurring_master?: boolean
}

// Orchestrator Command
interface OrchestratorCommand {
 type: string
 task_id?: string
 hold_id?: string
 hold_block?: HoldBlock
 date_key?: string
 title?: string
 slot?: { start: string; end: string }
 reason?: string
}

// Orchestrator Response
interface OrchestratorResponse {
 commands: OrchestratorCommand[]
 computed: {
 horizon_holds?: HoldBlock[]
 horizon_days?: string[]
 daily_stats?: Record<string, { holds: number; scheduled: number; available_minutes: number }>
 todays_focus?: any[]
 schedule_preview?: any[]
 unschedulable?: Array<{ task_id: string; reason: string }>
 }
}

// Scheduling hint from Planner LLM (Google Calendar RRULE format)
interface SchedulingHint {
 is_recurring: boolean
 rrule: string | null // e.g.,"RRULE:FREQ=DAILY" or"RRULE:FREQ=WEEKLY;BYDAY=MO,WE,FR"
 suggested_time_of_day:'morning' |'afternoon' |'evening' |'anytime' | null
 ideal_days: string[] | null // e.g., ["Monday","Wednesday","Friday"]
 rationale: string | null
}

// Full hierarchy context for AI scheduling
interface HierarchyContext {
 dream?: string
 dream_id?: number
 aspiration?: string
 aspiration_id?: number
 goal?: string
 goal_id?: number
 goal_priority?: string
 goal_target_date?: string
 parent_task?: string
 parent_task_id?: number
}

interface ItemToSchedule {
 id: number
 type: 'task' | 'action_item' | 'habit' | 'skill' | 'roadblock'
 title: string
 description?: string
 status?: string
 priority: string
 estimated_duration: number
 due_date?: string
 // Parent hierarchy info
 parent_task_id?: string
 parent_task_title?: string
 parent_goal_id?: string
 parent_goal_title?: string
 // Skill journey context
 parent_skill_id?: number
 parent_skill_title?: string
 is_skill_journey_task?: boolean
 // Full hierarchy context (for AI scheduling)
 hierarchy_context?: HierarchyContext
 // Scheduling hint from Planner LLM
 scheduling_hint?: SchedulingHint
 // Recurrence/scheduling preferences (from database - set by planner or manual entry)
 is_recurring?: boolean
 recurrence_pattern?: string
 recurrence_rule?: string | Record<string, any>
 ai_suggested_recurrence?: string | Record<string, any>
 // Direct scheduling hints (habits). `preferred_start_time` is "HH:MM"
 // and is treated by the AI scheduler as a hard-ish user/AI-confirmed
 // start time; `preferred_time_window` is softer and gets resolved
 // against the user's day anchors when no start time is set.
 preferred_start_time?: string
 preferred_time_window?: string
 preferred_days?: string[]
 duration_minutes?: number
}

const SCHEDULABLE_TYPES = ['task', 'habit', 'skill', 'roadblock'] as const

// Priority colors
const priorityColors: Record<string, string> = {
 urgent:'border-bad bg-bad/10',
 high:'border-warn bg-warn/10',
 medium:'border-brand bg-brand-subtle',
 low:'border-strong bg-surface'
}

const priorityBadgeColors: Record<string, string> = {
 urgent:'bg-bad',
 high:'bg-warn',
 medium:'bg-brand',
 low:'bg-muted'
}

const typeBadgeConfig: Record<string, { label: string; bgClass: string; textClass: string }> = {
 task: { label: 'Task', bgClass: 'bg-brand-subtle', textClass: 'text-brand' },
 habit: { label: 'Habit', bgClass: 'bg-ok/10', textClass: 'text-ok' },
 skill: { label: 'Skill', bgClass: 'bg-warn/10', textClass: 'text-warn' },
 roadblock: { label: 'Roadblock', bgClass: 'bg-bad/10', textClass: 'text-bad' },
 action_item: { label: 'Action', bgClass: 'bg-brand-subtle', textClass: 'text-brand' },
}

// Time slots for the day view (6am to 10pm)
// Full 24-hour timeline. Events outside the 6am-10pm "visible" band were
// previously clipped off the bottom/top of the calendar; this renders
// the whole day so the page can scroll to early-morning and late-night
// slots. Work-hour shading still highlights 9-18 inside the grid.
const HOUR_SLOTS = Array.from({ length: 24 }, (_, i) => i) // 0-23

const ITEMS_PER_PAGE = 10

const RECURRENCE_PATTERNS = [
 { value: null, label:'No recurrence' },
 { value:'daily', label:'Daily' },
 { value:'weekdays', label:'Weekdays (Mon-Fri)' },
 { value:'weekly', label:'Weekly' },
 { value:'biweekly', label:'Bi-weekly' },
 { value:'monthly', label:'Monthly' },
 { value:'custom', label:'Custom days' },
]

const DAYS_OF_WEEK = [
 { value: 0, label:'S', full:'Sun' },
 { value: 1, label:'M', full:'Mon' },
 { value: 2, label:'T', full:'Tue' },
 { value: 3, label:'W', full:'Wed' },
 { value: 4, label:'T', full:'Thu' },
 { value: 5, label:'F', full:'Fri' },
 { value: 6, label:'S', full:'Sat' },
]

// RRULE day abbreviation to day number (0=Sun, 1=Mon, etc.)
const RRULE_DAY_MAP: Record<string, number> = {
'SU': 0,'MO': 1,'TU': 2,'WE': 3,'TH': 4,'FR': 5,'SA': 6
}

// Parse RRULE string to extract recurrence pattern and days
const parseRrule = (rrule: string | null): { pattern: string | null; days: number[]; interval?: number } => {
 if (!rrule) return { pattern: null, days: [] }
 
 // Remove"RRULE:" prefix if present
 const ruleStr = rrule.replace(/^RRULE:/i,'')
 const parts = ruleStr.split(';')
 
 let freq =''
 let days: number[] = []
 let interval = 1
 
 for (const part of parts) {
 const [key, value] = part.split('=')
 switch (key.toUpperCase()) {
 case'FREQ':
 freq = value.toUpperCase()
 break
 case'BYDAY':
 days = value.split(',').map(d => {
 // Handle cases like"1MO" (first Monday) - just extract the day
 const dayMatch = d.match(/([A-Z]{2})$/)
 return dayMatch ? RRULE_DAY_MAP[dayMatch[1]] ?? -1 : -1
 }).filter(d => d >= 0)
 break
 case'INTERVAL':
 interval = parseInt(value) || 1
 break
 }
 }
 
 // Determine pattern based on FREQ and BYDAY
 let pattern: string | null = null
 
 if (freq ==='DAILY') {
 pattern ='daily'
 } else if (freq ==='WEEKLY') {
 if (interval === 2) {
 pattern ='biweekly'
 } else if (days.length === 0) {
 pattern ='weekly'
 } else if (days.length === 5 && [1, 2, 3, 4, 5].every(d => days.includes(d))) {
 // Mon-Fri = weekdays
 pattern ='weekdays'
 } else {
 pattern ='custom'
 }
 } else if (freq ==='MONTHLY') {
 pattern ='monthly'
 }
 
 return { pattern, days, interval }
}

// Get default time based on suggested_time_of_day
const getDefaultTimeFromHint = (timeOfDay: string | null): string => {
 switch (timeOfDay) {
 case'morning': return'09:00'
 case'afternoon': return'14:00'
 case'evening': return'19:00'
 default: return'09:00'
 }
}

// Hold start/end are stored as ISO datetimes from the orchestrator but as
// bare "HH:MM" on rows loaded from the schedule store; both shapes reach
// the same card.
const holdStartTime = (hold: HoldBlock): string =>
 hold.start.includes('T') ? hold.start.split('T')[1].substring(0, 5) : hold.start

const holdEndTime = (hold: HoldBlock): string =>
 hold.end.includes('T') ? hold.end.split('T')[1].substring(0, 5) : hold.end

export const ReviewSchedule: React.FC = () => {
 const navigate = useNavigate()
 const location = useLocation()
 
 // Use server time for consistent date handling (supports time simulation)
 const { serverDate, serverDateString, isLoading: serverTimeLoading } = useServerTime()
 
 // Get items from navigation state or fetch them
 // Schedulable types: tasks, habits, skills, roadblocks (NOT goals, dreams, aspirations, action_items)
 const passedItems = location.state?.items as ItemToSchedule[] | undefined
 const filteredPassedItems = passedItems?.filter(
   item => (SCHEDULABLE_TYPES as readonly string[]).includes(item.type)
 )
 
 const [items, setItems] = useState<ItemToSchedule[]>(filteredPassedItems || [])
 const [suggestions, setSuggestions] = useState<ScheduleSuggestion | null>(null)
 const [freeSlots, setFreeSlots] = useState<FreeSlot[]>([])
 const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([])
 const [loading, setLoading] = useState(true)
 const [applying, setApplying] = useState(false)
 const [error, setError] = useState<string | null>(null)
 
 // Orchestrator state - HOLD blocks
 const [holdBlocks, setHoldBlocks] = useState<HoldBlock[]>([])
 const [confirmingHolds, setConfirmingHolds] = useState(false)
 const [planningHorizon, setPlanningHorizon] = useState(false)
 // 'day' ≡ just the focused day, 'week' ≡ a rolling 7-day window starting
// today, 'month' ≡ the next 30 days, 'year' ≡ the next 365 days. The
// 'tomorrow' option is kept as a legacy value only for URL/deep-link
// compatibility; the UI no longer surfaces it.
type Horizon = 'day' | 'tomorrow' | 'week' | 'month' | 'year'
const [selectedHorizon, setSelectedHorizon] = useState<Horizon>('week')

 // Day view state - initialized from server time
 const [selectedDate, setSelectedDate] = useState<Date | null>(null)
 // Non-null date for calculations/rendering. selectedDate is null only until it is
 // initialized from server time.
 const displayDate = selectedDate ?? serverDate

 // Pagination state
 const [currentPage, setCurrentPage] = useState(1)
 const [searchQuery, setSearchQuery] = useState('')
 
 // Drag and drop state
 const [draggedItem, setDraggedItem] = useState<ItemToSchedule | null>(null)
 const [dropTargetHour, setDropTargetHour] = useState<number | null>(null)

 // Timeline is a 24-hour scroller (1440px tall at 60px/hour). We land the
 // user at 6am on mount so midnight–5am doesn't hog the viewport; they
 // can scroll up/down to cover the rest of the day. We only auto-scroll
 // once per selected date so a user who scrolls back to 1am to re-check
 // an early block isn't snapped away.
 const timelineScrollRef = useRef<HTMLDivElement | null>(null)
 const scrolledForDateRef = useRef<string | null>(null)

 // Auto-scroll the timeline to a sensible hour on date change. "Sensible"
 // means the earliest event's hour minus 1 (so the user can see context
 // above it), or 6am when there is nothing scheduled yet. Runs only
 // once per distinct date so we don't fight the user's manual scroll.
 useEffect(() => {
   const container = timelineScrollRef.current
   if (!container) return
   if (!selectedDate) return
   const dateKey = selectedDate.toISOString().slice(0, 10)
   if (scrolledForDateRef.current === dateKey) return
   // Cheap scan for earliest block on the selected day. Both calendar
   // events (HH:MM) and holds (ISO) carry a start time we can parse.
   let earliestHour: number | null = null
   const dayEvents = calendarEvents.filter(e => e.date === dateKey)
   for (const ev of dayEvents) {
     if (!ev.start) continue
     const [h] = ev.start.split(':').map(Number)
     if (earliestHour == null || h < earliestHour) earliestHour = h
   }
   for (const h of holdBlocks) {
     if (h.date_key !== dateKey) continue
     const iso = h.start
     if (!iso) continue
     const hh = parseInt(iso.split('T')[1]?.slice(0, 2) || '0', 10)
     if (Number.isFinite(hh)) {
       if (earliestHour == null || hh < earliestHour) earliestHour = hh
     }
   }
   const anchor = earliestHour != null ? Math.max(0, earliestHour - 1) : 6
   container.scrollTop = anchor * 60
   scrolledForDateRef.current = dateKey
 }, [selectedDate, calendarEvents, holdBlocks])
 
 // Selected item for detailed view
 const [selectedItem, setSelectedItem] = useState<ItemToSchedule | null>(null)
 
 // Selected HOLD block for invite-style modal
 const [selectedHold, setSelectedHold] = useState<HoldBlock | null>(null)
// Commitment-verb state. `holdNotice` is deliberately separate from
// `error`: "that day is already full, it stays flexible" is information,
// not a failure, and must not be dressed up in the red banner.
 const [holdVerbBusy, setHoldVerbBusy] = useState<string | null>(null)
 const [holdNotice, setHoldNotice] = useState<string | null>(null)
 const [confirmingAll, setConfirmingAll] = useState(false)
 const [clearingView, setClearingView] = useState(false)
// Move form (block sheet). Reseeded from whichever block is open, so a
// stale time from a previous block can never be submitted.
 const [moveDate, setMoveDate] = useState('')
 const [moveTime, setMoveTime] = useState('')
 const [moveKeepFlexible, setMoveKeepFlexible] = useState(false)
 useEffect(() => {
 setMoveDate(selectedHold?.date_key ??'')
 setMoveTime(selectedHold ? holdStartTime(selectedHold) :'')
 setMoveKeepFlexible(false)
 }, [selectedHold])
 
 // Quick schedule modal state - initialized from server time
 const [quickScheduleItem, setQuickScheduleItem] = useState<ItemToSchedule | null>(null)
 const [quickScheduleDate, setQuickScheduleDate] = useState('')
 const [quickScheduleTime, setQuickScheduleTime] = useState('09:00')
 const [quickScheduleDuration, setQuickScheduleDuration] = useState(30)
 
 // Initialize date states from server time when available
 useEffect(() => {
 if (!serverTimeLoading && serverDate && !selectedDate) {
 setSelectedDate(startOfDay(serverDate))
 setQuickScheduleDate(serverDateString)
 }
 }, [serverTimeLoading, serverDate, serverDateString, selectedDate])
 const [quickScheduleRecurrence, setQuickScheduleRecurrence] = useState<string | null>(null)
 const [quickScheduleCustomDays, setQuickScheduleCustomDays] = useState<number[]>([])
 const [quickScheduleEndDate, setQuickScheduleEndDate] = useState<string>('')
 
 // AI scheduling remaining items
 const [aiSchedulingRemaining, setAiSchedulingRemaining] = useState(false)
 
// There is no HOLD-vs-CONFIRM mode selector, and there should not be
// one: ratified decision #1 settles the question globally (assisted
// placement is FLEXIBLE) rather than making the user re-decide the
// product's doctrine on every click. The 2026-04 removal of the modal
// stands; what changed is which side of it we landed on.
const [rebalancing, setRebalancing] = useState(false)
 const [showRebalanceModal, setShowRebalanceModal] = useState(false)
 const [rebalanceScope, setRebalanceScope] = useState<'future' |'all'>('future')
 const [rebalanceIncludeToday, setRebalanceIncludeToday] = useState(false)
 const [rebalancePreview, setRebalancePreview] = useState<Array<{id: number, title: string, date: string, time_slot: string}>>([])
 const [loadingPreview, setLoadingPreview] = useState(false)
 const [rebalanceStep, setRebalanceStep] = useState<'options' |'preview'>('options')
 
 // Preferences - fetched from user profile
 const [workStart, setWorkStart] = useState('09:00')
 const [workEnd, setWorkEnd] = useState('18:00')
 const [userPrefs, setUserPrefs] = useState({
 daily_focus_limit: 3,
 daily_hold_limit: 3,
 max_parallel_tasks: 3,
 buffer_between_tasks: 15,
 default_horizon:'next_7_days' as'today' |'tomorrow' |'next_7_days' |'next_30_days'
 })
 const [focusPreference] = useState<'morning' |'afternoon' |'evening'>('morning')
 
 // Calculate end time from start time and duration
 const calculateEndTime = useCallback((startTime: string, durationMinutes: number): string => {
 try {
 const [hours, minutes] = startTime.split(':').map(Number)
 const totalMinutes = hours * 60 + minutes + durationMinutes
 const endHours = Math.floor(totalMinutes / 60) % 24
 const endMinutes = totalMinutes % 60
 return`${endHours.toString().padStart(2,'0')}:${endMinutes.toString().padStart(2,'0')}`
 } catch {
 return startTime
 }
 }, [])
 
 // Filter and paginate items
 const filteredItems = useMemo(() => {
 let result = items
 if (searchQuery) {
 const query = searchQuery.toLowerCase()
 result = items.filter(item => 
 item.title.toLowerCase().includes(query) ||
 item.description?.toLowerCase().includes(query)
 )
 }
 return result
 }, [items, searchQuery])
 
// Typed keys (`task:42`, `habit:99`) for every yanamas calendar block so
// we can exclude already-scheduled items from the "To Schedule" list
// without task-id and habit-id colliding. Habit occurrence blocks rarely
// populate parent_task_id and instead carry their origin on
// source_item_id / recurring_source_id, which is why the prior
// parent_task_id-only filter let habits leak through.
const scheduledItemKeys = useMemo(
  () => buildScheduledItemKeys(calendarEvents),
  [calendarEvents]
)

// Filter out stale holds: once an item has a confirmed block, its hold is
// stale. Keyed by type+id — AI Assist now places habits, skills and
// roadblocks as holds too, and a bare id would let task 7's confirmed
// block retire habit 7's hold.
const effectiveHoldBlocks = useMemo(() => {
return holdBlocks.filter(h => !scheduledItemKeys.has(`${h.item_type}:${h.task_id}`))
}, [holdBlocks, scheduledItemKeys])

const holdItemKeys = useMemo(
() => new Set(effectiveHoldBlocks.map(h => `${h.item_type}:${h.task_id}`)),
[effectiveHoldBlocks]
)

// Separate items into: on hold, scheduled (confirmed), and unscheduled
const itemsOnHold = useMemo(() => {
return filteredItems.filter(item => holdItemKeys.has(`${item.type}:${item.id}`))
}, [filteredItems, holdItemKeys])

const itemsScheduled = useMemo(() => {
const scheduledIds = new Set(suggestions?.scheduled_items?.map(s => s.item_id) || [])
return filteredItems.filter(item => scheduledIds.has(item.id))
}, [filteredItems, suggestions])

const itemsToSchedule = useMemo(() => {
const scheduledIds = new Set(suggestions?.scheduled_items?.map(s => s.item_id) || [])
return filteredItems.filter(item =>
!holdItemKeys.has(`${item.type}:${item.id}`) &&
!scheduledIds.has(item.id) &&
!scheduledItemKeys.has(`${item.type}:${item.id}`)
)
}, [filteredItems, holdItemKeys, suggestions, scheduledItemKeys])

 // Get hold info for an item
 const getHoldForItem = useCallback((item: ItemToSchedule): HoldBlock | undefined => {
 return effectiveHoldBlocks.find(
 h => h.task_id === String(item.id) && h.item_type === item.type
 )
 }, [effectiveHoldBlocks])
 
 const totalPages = Math.ceil(itemsToSchedule.length / ITEMS_PER_PAGE)
 const paginatedItems = useMemo(() => {
 const start = (currentPage - 1) * ITEMS_PER_PAGE
 return itemsToSchedule.slice(start, start + ITEMS_PER_PAGE)
 }, [itemsToSchedule, currentPage])
 
 // Get scheduled items for selected date
 const scheduledItemsForDate = useMemo(() => {
 if (!suggestions) return []
 return suggestions.scheduled_items
 .filter(item => item.suggested_date === format(displayDate,'yyyy-MM-dd'))
 .sort((a, b) => a.suggested_time.localeCompare(b.suggested_time))
 }, [suggestions, displayDate])

 // Get HOLD blocks for selected date (excludes stale holds for confirmed tasks)
 const holdsForDate = useMemo(() => {
 const dateStr = format(displayDate,'yyyy-MM-dd')
 return effectiveHoldBlocks
 .filter(h => h.date_key === dateStr)
 .sort((a, b) => a.start.localeCompare(b.start))
 }, [effectiveHoldBlocks, displayDate])

 // Get calendar events for selected date
 const eventsForDate = useMemo(() => {
 return calendarEvents.filter(event =>
 isSameDay(parseISO(event.date), displayDate)
 )
 }, [calendarEvents, displayDate])

// Phase 4 — split counters.
//
// The old header conflated tasks and habits under one "scheduled" /
// "remaining" pair, which is misleading now that Phase 1 locked the
// daily_focus_limit to task-like items only. A user with 5 daily habits
// and a 3/day task budget would see "8 remaining" and wonder why
// yanamas only plans 3.
//
// We split the math into:
//   - tasksScheduledNow / tasksRemaining: AI suggestions vs unscheduled
//     candidates, limited to task-like items.
//   - habitsPlanned: AI suggestions that are habits (shown separately
//     as "not counted" so the user understands they don't eat the
//     focus budget).
//   - confirmedTasksTotal / confirmedHabitsTotal: yanamas calendar
//     blocks already in place, split the same way.
const _isHabitItem = (item: ItemToSchedule) => item.type === 'habit'
const itemsScheduledTasks = useMemo(
  () => itemsScheduled.filter(i => !_isHabitItem(i)),
  [itemsScheduled]
)
const itemsScheduledHabits = useMemo(
  () => itemsScheduled.filter(_isHabitItem),
  [itemsScheduled]
)
const itemsToScheduleTasks = useMemo(
  () => itemsToSchedule.filter(i => !_isHabitItem(i)),
  [itemsToSchedule]
)
const confirmedTasksTotal = useMemo(() => {
  return calendarEvents.filter(
    e => !e.external_system && e.source === 'horton' && e.task_kind !== 'habit_occurrence'
  ).length
}, [calendarEvents])
const confirmedHabitsTotal = useMemo(() => {
  return calendarEvents.filter(
    e => !e.external_system && e.source === 'horton' && e.task_kind === 'habit_occurrence'
  ).length
}, [calendarEvents])

// Gate for the optional "Confirm all" action. NOT rendered as a badge or
// a count of things owed — SC-6 forbids a surface that tallies
// unconfirmed holds at the user.
const flexibleHoldCount = useMemo(
  () => effectiveHoldBlocks.filter(h => h.commitment === 'flexible').length,
  [effectiveHoldBlocks]
)
 
 // Get free slots for selected date
 const freeSlotsForDate = useMemo(() => {
 const dateStr = format(displayDate,'yyyy-MM-dd')
 return freeSlots.filter(slot => slot.date === dateStr)
 }, [freeSlots, displayDate])
 
 // Check if an item is already scheduled
 const isItemScheduled = useCallback((itemId: number) => {
 return suggestions?.scheduled_items.some(s => s.item_id === itemId) || false
 }, [suggestions])
 
 // On mount: Fetch items + existing calendar, then call Orchestrator
 useEffect(() => {
 fetchInitialData()
 }, [])
 
 // Call Orchestrator plan-horizon when items are loaded
 const planHorizon = async (horizonType: Horizon = selectedHorizon) => {
 if (items.length === 0) return
 
 try {
 setPlanningHorizon(true)
 setError(null)
 
 // Build planner_output from items (tasks from DB)
 // Sort by priority and deadline for better scheduling order
 const sortedItems = [...items].sort((a, b) => {
 // Priority order: critical > high > medium > low
 const priorityOrder: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 }
 const aPriority = priorityOrder[a.priority ||'medium'] || 2
 const bPriority = priorityOrder[b.priority ||'medium'] || 2
 if (bPriority !== aPriority) return bPriority - aPriority
 
 // Then by deadline (sooner first)
 if (a.due_date && b.due_date) {
 return new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
 }
 if (a.due_date) return -1
 if (b.due_date) return 1
 return 0
 })
 
 const plannerOutput = {
 state: {
 items: sortedItems.map((item, idx) => ({
 id: item.id.toString(),
 type: item.type,
 title: item.title,
 fields: {
 status: item.status ||'pending',
 schedule_candidate: true,
 duration_minutes: item.estimated_duration || 30,
 priority: item.priority ||'medium',
 deadline: item.due_date || null,
 description: item.description ||'',
 // Execution order based on sorted position
 execution_order: idx + 1
 }
 })),
 task_action_items: {}
 }
 }
 
// Build ui_state with existing blocks.
// Phase 2: missed/expired (and cancelled) rows are terminal — they
// remain visible in the calendar for the user but must NOT be sent
// as live commitments, or the orchestrator would keep suppressing
// re-planning for their parent tasks. The server-side filter in
// `_get_already_scheduled_task_ids` is a belt-and-braces guard for
// any legacy clients that don't apply this filter.
const existingBlocks = calendarEvents
.filter(e => (e.block_status ??'confirmed') ==='confirmed')
.map(e => ({
id:`cal-${e.date}-${e.start}`,
start:`${e.date}T${e.start ||'09:00'}:00`,
end:`${e.date}T${e.end ||'10:00'}:00`,
title: e.title,
block_type:'task',
status:'confirmed'
}))
 
 const uiState = {
 schedule_tab: {
 existing_calendar_blocks: existingBlocks,
 user_created_blocks: [],
 // Send ALL existing holds to backend - it will preserve user-modified ones
 // and re-use existing holds instead of creating duplicates
 hold_blocks: effectiveHoldBlocks.map(h => ({
 id: h.id,
 task_id: h.task_id,
 start: h.start,
 end: h.end,
 date_key: h.date_key,
 title: h.title,
 block_type: h.block_type,
 status: h.status,
 source: h.source,
 user_modified: h.user_modified
 }))
 },
 preferences: {
 timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
 wake_time: workStart,
 sleep_time: workEnd,
 // Use actual user preferences - these control how many tasks are placed per day
 daily_focus_limit: userPrefs.daily_focus_limit,
 daily_hold_limit: userPrefs.daily_hold_limit,
 max_parallel_tasks: userPrefs.max_parallel_tasks,
 buffers_minutes: userPrefs.buffer_between_tasks
 },
today: serverDateString, // Use server time for consistent date handling
user_intent: {
mode:'plan_horizon',
// The backend's 'horizon' field still speaks the legacy vocabulary
// (today/tomorrow/next_7_days/next_30_days). Map the new UI values
// back to those so we don't force a server contract change.
horizon:
  horizonType === 'day' ? 'today' :
  horizonType === 'week' ? 'next_7_days' :
  horizonType === 'month' ? 'next_30_days' :
  horizonType === 'year' ? 'next_365_days' :
  horizonType,
horizon_days:
  horizonType === 'day' ? 1 :
  horizonType === 'tomorrow' ? 2 :
  horizonType === 'week' ? 7 :
  horizonType === 'month' ? 30 :
  horizonType === 'year' ? 365 : 30
}
}
 
 const response = await fetch('/api/orchestrator/plan-horizon', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify({
 planner_output: plannerOutput,
 ui_state: uiState
 })
 })
 
 const data = await response.json()
 
 if (data.success && data.data) {
 const result = data.data as OrchestratorResponse
 
 // Extract hold blocks from commands or computed
 const newHolds: HoldBlock[] = []
 
 // From computed.horizon_holds
 if (result.computed?.horizon_holds) {
 result.computed.horizon_holds.forEach((hold: any) => {
 // Calculate duration from start/end if not provided
 let duration = hold.duration_minutes || 30
 if (!hold.duration_minutes && hold.start && hold.end) {
 try {
 const startMs = new Date(hold.start).getTime()
 const endMs = new Date(hold.end).getTime()
 duration = Math.round((endMs - startMs) / 60000) || 30
 } catch { duration = 30 }
 }
 
 newHolds.push({
 id: hold.hold_id || hold.id, // Backend uses hold_id
 task_id: hold.task_id,
 item_type:'task',
 title: hold.title || items.find(i => i.id.toString() === hold.task_id)?.title ||'Task',
 start: hold.start,
 end: hold.end,
 date_key: hold.date_key,
 block_type:'hold',
 status:'proposed',
 source: hold.source ||'horton_auto',
 duration_minutes: duration, // Use calculated duration
 user_modified: hold.user_modified,
 origin:'orchestrator_plan',
 commitment:'flexible',
 commitment_source:'ai_proposal',
 block_id: null
 })
 })
 }
 
 // From CREATE_HOLD_BLOCK commands
 result.commands.forEach((cmd: any) => {
 if (cmd.type ==='CREATE_HOLD_BLOCK' && cmd.hold_block) {
 const hb = cmd.hold_block
 const holdId = cmd.hold_id || hb.id || hb.hold_id // Use command's hold_id first
 if (!newHolds.find(h => h.id === holdId)) {
 newHolds.push({
 id: holdId,
 task_id: cmd.task_id || hb.task_id,
 item_type:'task',
 title: cmd.title || hb.title || items.find(i => i.id.toString() === (cmd.task_id || hb.task_id))?.title ||'Task',
 start: cmd.start || hb.start,
 end: cmd.end || hb.end,
 date_key: cmd.date_key || hb.date_key,
 block_type:'hold',
 status:'proposed',
 source: hb.source ||'horton_auto',
 duration_minutes: hb.duration_minutes || 30,
 user_modified: hb.user_modified,
 origin:'orchestrator_plan',
 commitment:'flexible',
 commitment_source:'ai_proposal',
 block_id: null
 })
 }
 }
 })
 
 setHoldBlocks(newHolds)

 console.log(`✅ Orchestrator planned ${newHolds.length} HOLD blocks`)
 } else {
 setError(data.error ||'Failed to plan schedule')
 }
 } catch (err) {
 console.error('Error calling orchestrator:', err)
 setError('Failed to connect to scheduling service')
 } finally {
 setPlanningHorizon(false)
 }
 }
 
// ══ Flexible → Confirmed verbs (constitution §3, ratified decision #1) ══
//
// yanamas-assisted placement produces Flexible holds; these are the ways a
// user settles one. None of them is required. SC-6 zero-nag: a hold the
// user never touches is a valid way to live, so nothing below is surfaced
// as an outstanding chore, a count of unconfirmed blocks, or a prompt.
//
// A verb here is addressed to a ROW, not to an item, whenever this page
// knows the row. That distinction is the whole of C6/C8: the item-scoped
// endpoint resolves an item to whichever live row sorts first, which is
// routinely a different day than the card that was clicked, and for a
// habit/skill/roadblock is a different ITEM entirely (see
// `canSetItemCommitment`). Where no row id exists the verb is withheld
// rather than pointed somewhere plausible.
//
// Which verb can reach a block depends on where the block lives:
//   origin 'orchestrator_plan' — never persisted; the whole hold is
//     materialized through /api/orchestrator/confirm-holds.
//   origin 'schedule_store' WITH block_id — the row is directly
//     addressable: confirm by id, move it, take it off the day. If that id
//     names a recurring MASTER, both confirm and skip change meaning —
//     see `is_recurring_master`.
//   origin 'schedule_store' WITHOUT block_id — a virtual occurrence of a
//     rule. Reachable by item id only for tasks; Move, Remove and the
//     commitment flip are withheld for everything else.

// Whether the item-scoped commitment flip can honestly reach this hold.
// Used both to gate the call and to withhold the control, so the button
// and the guard cannot disagree.
 const itemScopeReaches = (hold: HoldBlock): boolean =>
 hold.origin ==='schedule_store' && canSetItemCommitment(hold.item_type)

// Materialize ephemeral plan-horizon holds as confirmed calendar blocks.
 const confirmHolds = async (holdsToConfirm: HoldBlock[]) => {
 if (holdsToConfirm.length === 0) return false

 try {
 setConfirmingHolds(true)
 setError(null)

 const response = await fetch('/api/orchestrator/confirm-holds', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify({
 planner_output: { state: { items: [], task_action_items: {} } },
 ui_state: {
 schedule_tab: {
 existing_calendar_blocks: [],
 user_created_blocks: [],
 hold_blocks: holdsToConfirm
 },
 preferences: {
 timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
 wake_time: workStart,
 sleep_time: workEnd
 },
 today: serverDateString, // Use server time for consistent date handling
 user_intent: {
 mode:'confirm_holds',
 selected_hold_ids: holdsToConfirm.map(h => h.id)
 }
 }
 })
 })
 
 const data = await response.json()

 if (data.success) {
 setHoldBlocks(prev => prev.filter(h => !holdsToConfirm.some(c => c.id === h.id)))
 await fetchExistingSchedule()
 return true
 }
 setError(data.error ||'Could not confirm those times')
 return false
 } catch (err) {
 console.error('Error confirming holds:', err)
 setError('Could not reach the scheduler')
 return false
 } finally {
 setConfirmingHolds(false)
 }
 }

// Take a Flexible block off the plan.
//
// The verb is the day-scoped skip, deliberately, and NOT the store's
// `supersede_proposed_holds`. Both leave the row alive (SC-12 — expired
// or superseded, never deleted), but they record different events, and
// the SC-16 outcome has to name the one that actually happened:
//
//   `superseded_by_replan` says HORTON replaced this hold with a better
//   one. It is Horton's own act, carries zero friction evidence by
//   SC-17, and stamping it here would erase the user from their own
//   decision.
//   `skipped_by_user` says the USER dismissed this occurrence. That is
//   what a click on Remove is. It is scoped to the one day (SC-15 — the
//   parent task, its due date and its series are untouched), it gives
//   the time back, and `skipped_for_day` is terminal, so the released
//   hold stops reading as live work on Today.
//
// Neither outcome is punished and neither prompts a follow-up (SC-6):
// recording the user act honestly is not the same as holding it against
// them, and Phase 4 is where a skip acquires any weight at all.
// One more thing this verb is NOT: a way to stop a rhythm. On a recurring
// hold the server materializes THAT DAY as an exception and skips it; the
// rule survives and keeps producing holds. So on a series the label is
// "Not today" and the card stays — dropping it would say the rhythm is
// gone while yanamas is still holding time for it tomorrow (C12/C21).
// Ending a rhythm is a decision about the habit itself and does not
// belong to a square on a planning grid.
 const removeHold = async (hold: HoldBlock) => {
 if (hold.origin !=='schedule_store') {
 // Never persisted — a plan-horizon hold exists only in this
 // session's state, so dropping the card leaves nothing behind.
 setHoldBlocks(prev => prev.filter(h => h.id !== hold.id))
 return
 }
 if (hold.block_id == null) {
 // A real stored row this client cannot address. Drop the card the
 // user asked to be rid of, but do not let it vanish quietly as if
 // the time had been given back — it has not.
 setHoldBlocks(prev => prev.filter(h => h.id !== hold.id))
 setHoldNotice(`Removed from this view — ${BRAND.assistant} is still holding that time. Re-plan to reach it again.`)
 return
 }
 setHoldVerbBusy(hold.id)
 setError(null)
 setHoldNotice(null)
 try {
 // Any 2xx means the block is off the day — including the idempotent
 // replay and the "it already left another way" case. Only drop it
 // locally once the server agrees, or the card would vanish while the
 // block stayed on the calendar.
 const result = await removeBlockFromDay(hold.block_id, hold.date_key)
 if (!result.ok) {
 setError(result.error ||'Could not take that off the day')
 return
 }
 if (releaseEndsHold(hold)) {
 setHoldBlocks(prev => prev.filter(h => h.id !== hold.id))
 } else {
 setHoldNotice(`Off that day. This one repeats, so ${BRAND.assistant} keeps holding time for it — change the rhythm on the item itself.`)
 }
 // The row is terminal now; the day's other surfaces must stop
 // showing it. Without this the card goes but Today still lists it.
 await fetchExistingSchedule()
 } finally {
 setHoldVerbBusy(null)
 }
 }

// Settle one Flexible block. Dispatches on how the block is addressable —
// see the origin note above.
//
// On a recurring master this confirms the whole rhythm, because that is
// what the id addresses. The control that calls it says so in its own
// label (`confirmVerbLabel`); nothing here quietly turns a click on one
// day's card into a commitment to every Tuesday for a year.
 const confirmHold = async (hold: HoldBlock) => {
 if (hold.origin ==='schedule_store' && hold.block_id == null && !itemScopeReaches(hold)) {
 // A stored row with no address this page can use, on an item the
 // item-scoped flip cannot reach either. Confirming by date span
 // would sweep in rows this session never displayed (C6), and the
 // item-scoped call would land on whatever task shares the id (C8).
 setHoldNotice('Could not reach that one from here — it stays flexible.')
 return
 }
 setHoldVerbBusy(hold.id)
 setError(null)
 setHoldNotice(null)
 try {
 if (hold.origin ==='orchestrator_plan') {
 await confirmHolds([hold])
 return
 }

 if (hold.block_id != null) {
 const result = await confirmHoldsScoped({ block_ids: [hold.block_id] })
 if (!result.ok) {
 setError(result.error ||'Could not confirm that time')
 return
 }
 if (result.rejected.length > 0) {
 setHoldNotice(result.rejected[0].reason ||'That day is already full — it stays flexible.')
 return
 }
 if (!result.applied) {
 setHoldNotice(result.message ||'Saved — not on the calendar yet.')
 return
 }
 if (hold.is_recurring_master) {
 setHoldNotice(`Confirmed every time this repeats. ${BRAND.assistant} will plan around the whole rhythm.`)
 }
 } else {
 const result = await setItemCommitment(
 Number(hold.task_id),'confirmed', hold.item_type,
 )
 if (!result.ok) {
 setError(result.error ||'Could not confirm that time')
 return
 }
 if (!result.applied) {
 setHoldNotice(result.message ||'Saved — not on the calendar yet.')
 return
 }
 }

 setHoldBlocks(prev => prev.map(h =>
 h.id === hold.id
 ? { ...h, commitment:'confirmed' as const, commitment_source:'user_confirmation' as const, status:'confirmed' as const }
 : h
 ))
 await fetchExistingSchedule()
 } finally {
 setHoldVerbBusy(null)
 }
 }

// "Keep flexible" — the SC-7 counterpart to Confirm, and a no-op by
// design when the block is already Flexible. yanamas keeps the right to
// re-optimize it; there is nothing to write and nothing to ask.
//
// Handing a CONFIRMED block back is a real write, and the only endpoint
// that performs it is item-scoped: it flips whichever live row for that
// item sorts first. So it is used only where that resolution is
// trustworthy — a task-backed hold — and `expectedBlockId` makes the
// server prove it touched the row this card names. Everywhere else the
// verb is withheld (`canKeepFlexible` below) rather than aimed at a row
// the user is not looking at.
 const keepHoldFlexible = async (hold: HoldBlock) => {
 if (hold.commitment ==='flexible') {
 setHoldNotice(`Kept flexible — ${BRAND.assistant} can still move this.`)
 return
 }
 if (!itemScopeReaches(hold)) {
 setHoldNotice('Could not reach that one from here — nothing changed.')
 return
 }
 setHoldVerbBusy(hold.id)
 setError(null)
 setHoldNotice(null)
 try {
 const result = await setItemCommitment(
 Number(hold.task_id),'flexible', hold.item_type, hold.block_id,
 )
 if (!result.ok) {
 setError(result.error ||`Could not hand that back to ${BRAND.assistant}`)
 return
 }
 // The 202 `applied:false` contract: restyling the card here would
 // claim a stored state the server never reported, and the chip is
 // the only place the user can read what yanamas is allowed to move.
 if (!mayRenderAsApplied(result)) {
 setHoldNotice(result.message ||'Saved — not on the calendar yet.')
 await fetchExistingSchedule()
 return
 }
 setHoldBlocks(prev => prev.map(h =>
 h.id === hold.id
 ? { ...h, commitment:'flexible' as const, commitment_source:'ai_proposal' as const, status:'proposed' as const }
 : h
 ))
 setHoldNotice(`Kept flexible — ${BRAND.assistant} can still move this.`)
 } finally {
 setHoldVerbBusy(null)
 }
 }

// Whether the "keep it flexible" control has anywhere to point. Always
// true for a block that is already Flexible — there it is an affirmation
// that writes nothing, and saying so is the honest answer to a click.
 const canKeepFlexible = (hold: HoldBlock): boolean =>
 hold.commitment ==='flexible' || itemScopeReaches(hold)

// What Confirm commits, said in the button. A recurring hold's id
// addresses the RULE, so the control must not read like a decision about
// one day (C7).
 const confirmVerbLabel = (hold: HoldBlock): string =>
 hold.is_recurring_master ?'Confirm the whole rhythm' :'Confirm'

// Re-place one Flexible block. Only reachable for blocks with a row id;
// SC-7 makes a user-chosen time Confirmed unless they ask to keep it
// flexible.
 const moveHold = async (hold: HoldBlock, date: string, time: string, keepFlexible: boolean) => {
 if (hold.block_id == null) return
 setHoldVerbBusy(hold.id)
 setError(null)
 setHoldNotice(null)
 try {
 const endTime = calculateEndTime(time, hold.duration_minutes)
 const result = await moveBlock(hold.block_id, {
 date,
 timeSlot:`${time}-${endTime}`,
 durationMin: hold.duration_minutes,
 keepFlexible,
 })
 if (!result.ok) {
 setError(result.error ||'Could not move that block')
 return
 }
 if (!result.applied) {
 setHoldNotice(result.message ||'Saved — not on the calendar yet.')
 return
 }
 setHoldBlocks(prev => prev.map(h =>
 h.id === hold.id
 ? {
 ...h,
 date_key: date,
 start:`${date}T${time}:00`,
 end:`${date}T${endTime}:00`,
 commitment: keepFlexible ?'flexible' as const :'confirmed' as const,
 commitment_source: keepFlexible ? h.commitment_source :'user_direct' as const,
 status: keepFlexible ?'proposed' as const :'confirmed' as const,
 }
 : h
 ))
 setSelectedHold(null)
 await fetchExistingSchedule()
 } finally {
 setHoldVerbBusy(null)
 }
 }

// Confirm the Flexible blocks THIS PAGE IS SHOWING. "All" means the
// cards on screen, never "every hold you own".
//
// The scope has to carry that meaning, because a date scope does not: it
// confirms whatever proposed rows the server finds in that span,
// including holds from an earlier session this page never rendered. Every
// one of those becomes a commitment the user never saw and never
// accepted — SC-6's other direction, where instead of nagging for
// confirmation Horton manufactures it. Confirming by `block_ids` says
// exactly which blocks, and nothing else moves.
//
// A hold with no row id cannot be named that way, so it is simply not
// confirmed. There is deliberately no date-scoped second pass: covering
// those holds means covering everything else in the span too, and the
// user is told what was left out instead of being given a number that
// includes blocks they never saw.
//
// Recurring holds are held back for the same class of reason. Their id
// addresses the RULE, so sweeping one into a batch would turn "confirm
// what's on screen" into a commitment to every day the rule will ever
// produce. Those stay a per-card decision, where the button can say so.
 const confirmAllFlexible = async () => {
 const flexible = effectiveHoldBlocks.filter(h => h.commitment ==='flexible')
 if (flexible.length === 0) return
 setConfirmingAll(true)
 setError(null)
 setHoldNotice(null)
 try {
 const ephemeral = flexible.filter(h => h.origin ==='orchestrator_plan')
 const stored = flexible.filter(h => h.origin ==='schedule_store')

 if (ephemeral.length > 0) {
 await confirmHolds(ephemeral)
 }

 const { addressable, series, unaddressable } = partitionForConfirm(stored)

 let confirmedCount = 0
 let rejectedCount = 0
 const settled = new Set<string>()

 if (addressable.length > 0) {
 const result = await confirmHoldsScoped({
 block_ids: addressable.map(h => h.block_id as number),
 })
 if (!result.ok) {
 setError(result.error ||'Could not confirm those times')
 return
 }
 if (!result.applied) {
 setHoldNotice(result.message ||'Saved — not on the calendar yet.')
 return
 }
 confirmedCount = result.confirmed_count
 rejectedCount = result.rejected.length

// The server named the rows it stamped, so only those cards leave.
// A block that was neither confirmed nor rejected (it had already
// stopped being a hold) keeps its card until the refetch settles it.
 const confirmedIds = new Set(result.confirmed_ids)
 addressable.forEach(h => {
 if (h.block_id != null && confirmedIds.has(h.block_id)) settled.add(h.id)
 })
 }

 if (settled.size > 0) {
 setHoldBlocks(prev => prev.filter(h => !settled.has(h.id)))
 }

// Say what stayed behind, in the register the Clear path uses for the
// same situation. Never a chore, never a count of things still owed —
// just the honest boundary of what this button reached (SC-6).
 const notes: string[] = []
 if (rejectedCount > 0) {
 notes.push(`${rejectedCount} stayed flexible — those days are already full.`)
 }
 if (series.length > 0) {
 notes.push(`${series.length} repeat${series.length === 1 ?'s' :''} — open ${series.length === 1 ?'it' :'them'} to confirm the whole rhythm.`)
 }
 if (unaddressable.length > 0) {
 notes.push(`${unaddressable.length} could not be reached from here and stay flexible.`)
 }
 if (notes.length > 0) {
 setHoldNotice(`${confirmedCount} confirmed. ${notes.join(' ')}`)
 }
 await fetchExistingSchedule()
 } finally {
 setConfirmingAll(false)
 }
 }

// "Clear" — discard the plan on screen.
//
// Under HOLD-first the plan is not a preview: every Flexible card is a
// real row Horton wrote, so emptying the list without releasing those
// rows left them live. They kept their slot, kept surfacing as Flexible
// on Today, and the only affordance that could have removed them — the
// card's own Remove — had just been cleared away. That is the residual
// leak, and the honest fix is for Clear to perform the same release
// Remove does, one card at a time.
//
// Twice scoped, and both scopes matter:
//
//   - to the cards actually on screen (`effectiveHoldBlocks`). A hold
//     this page filtered out of the view is one the user never saw, and
//     stamping `skipped_by_user` on it would invent a dismissal — the
//     same mistake as confirming a hold nobody looked at, pointed the
//     other way.
//   - to the ones still FLEXIBLE. A card the user confirmed is their
//     commitment now, not Horton's proposal (SC-5), and discarding a
//     plan must never quietly cancel it. Those rows are real calendar
//     blocks and are meant to outlive this view; taking one off a day
//     stays a per-card decision.
//
//   - to the ones that are a single dated block. A recurring hold cannot
//     be discarded one day at a time: the day-scoped release takes one
//     occurrence and the rule immediately produces the next. Clearing the
//     card would leave the user with a rhythm still holding time and no
//     card left to reach it from, so those keep their card and are named
//     in the notice (C12/C21).
//
// Anything that could not be released is counted and said out loud
// rather than left to be discovered on Today.
 const clearPlanningView = async () => {
 const onScreen = effectiveHoldBlocks.filter(h => h.commitment ==='flexible')
 const stored = onScreen.filter(h => h.origin ==='schedule_store')
 const { addressable: releasable, series, unaddressable } = partitionForConfirm(stored)

 setClearingView(true)
 setError(null)
 setHoldNotice(null)
 try {
 let failed = 0
 for (const hold of releasable) {
 const result = await removeBlockFromDay(hold.block_id as number, hold.date_key)
 if (!result.ok) failed += 1
 }

 // Every card goes except the rhythms, which are still holding time.
 const kept = new Set(series.map(h => h.id))
 setHoldBlocks(prev => prev.filter(h => kept.has(h.id)))
 setSuggestions({ scheduled_items: [], optimization_notes:'', warnings: [], unscheduled_items: [] })
 setSelectedItem(null)
 setSelectedHold(null)

 const stranded = failed + unaddressable.length
 const notes: string[] = []
 if (stranded > 0) {
 notes.push(`${stranded} held time${stranded === 1 ?'' :'s'} could not be released and stay on your calendar.`)
 }
 if (series.length > 0) {
 notes.push(`${series.length} repeat${series.length === 1 ?'s' :''} and stay${series.length === 1 ?'s' :''} on your plan — change the rhythm on the item itself.`)
 }
 if (notes.length > 0) {
 setHoldNotice(`Cleared. ${notes.join(' ')}`)
 }
 await fetchExistingSchedule()
 } finally {
 setClearingView(false)
 }
 }

 // Fetch existing calendar events and schedule entries (Step 1)
 const fetchExistingSchedule = async () => {
 try {
 // Calculate date range - get 90 days ahead for planning view
 // Use server time for consistent date handling
 const startDate = serverDate
 const endDate = addDays(serverDate, 90)
 const startStr = format(startDate,'yyyy-MM-dd')
 const endStr = format(endDate,'yyyy-MM-dd')
 
 // Fetch user's existing schedule entries and synced calendar events
 const [scheduleRes, freeSlotsRes] = await Promise.all([
 fetch(`/api/schedule/entries?start=${startStr}&end=${endStr}`, { credentials:'include' }),
 fetch(`/api/ai-scheduler/free-slots?days_ahead=7&work_start=${workStart}&work_end=${workEnd}`, { credentials:'include' })
 ])
 
 const scheduleData = await scheduleRes.json()
 const freeSlotsData = await freeSlotsRes.json()
 
 // Set calendar events from schedule entries
 if (scheduleData.success && scheduleData.entries) {
 console.log('📅 ReviewSchedule: Loaded', scheduleData.entries.length,'schedule entries')
 const events: CalendarEvent[] = scheduleData.entries.map((entry: any) => {
 // Parse time_slot (e.g.,"09:00-10:00" or"09:00") into start/end
 let startTime ='09:00'
 let endTime ='10:00'
 if (entry.time_slot) {
 const parts = entry.time_slot.split('-')
 startTime = parts[0]?.trim() ||'09:00'
 if (parts[1]) {
 endTime = parts[1].trim()
 } else if (entry.duration) {
 // Calculate end time from start + duration
 const [h, m] = startTime.split(':').map(Number)
 const totalMins = h * 60 + m + (entry.duration || 30)
 endTime =`${Math.floor(totalMins / 60).toString().padStart(2,'0')}:${(totalMins % 60).toString().padStart(2,'0')}`
 }
 }
 
return {
id: entry.id,
title: entry.title || entry.activity,
date: entry.date,
start: startTime,
end: endTime,
source: (entry.external_system ?'google_calendar' :'horton') as'google_calendar' |'horton',
block_type: entry.block_type,
block_status: entry.block_status,
block_source: entry.block_source,
external_system: entry.external_system,
parent_task_id: entry.parent_task_id,
// Needed so buildScheduledItemKeys can exclude already-scheduled
// habits from the "To Schedule" list. Habit occurrence blocks
// often leave parent_task_id null and instead carry their origin
// on source_item_id / recurring_source_id.
task_kind: entry.task_kind,
source_item_id: entry.source_item_id,
source_item_type: entry.source_item_type,
recurring_source_id: entry.recurring_source_id,
}
})
 console.log('📅 ReviewSchedule: Parsed events:', events.slice(0, 3))
 setCalendarEvents(events)
 }
 
 // Set free slots
 if (freeSlotsData.success && freeSlotsData.free_slots) {
 setFreeSlots(freeSlotsData.free_slots)
 }
 } catch (err) {
 console.error('Error fetching existing schedule:', err)
 }
 }
 
 // Fetch all initial data: items + existing schedule, then call Orchestrator
 // Fetch user scheduling preferences
 const fetchUserPreferences = async () => {
 try {
 const res = await fetch('/api/scheduling-preferences', { credentials:'include' })
 if (res.ok) {
 const prefs = await res.json()
 setUserPrefs({
 daily_focus_limit: prefs.daily_focus_limit || 3,
 daily_hold_limit: prefs.daily_hold_limit || 3,
 max_parallel_tasks: prefs.max_parallel_tasks || 3,
 buffer_between_tasks: prefs.buffer_between_tasks || 15,
 default_horizon: prefs.default_horizon ||'next_7_days'
 })
setWorkStart(prefs.work_start_time ||'09:00')
setWorkEnd(prefs.work_end_time ||'18:00')
// Use default horizon from preferences. The backend persists the
// legacy vocabulary ('today'/'next_7_days'/...), but the UI switcher
// speaks 'day'/'week'/'month'/'year'. Translate here so the saved
// preference actually highlights the right tab and scopes Re-Plan
// correctly. Anything unrecognised falls through to the state default.
const backendToUi: Record<string, Horizon> = {
  today: 'day',
  tomorrow: 'tomorrow',
  next_7_days: 'week',
  next_30_days: 'month',
  next_365_days: 'year',
}
if (prefs.default_horizon && backendToUi[prefs.default_horizon]) {
setSelectedHorizon(backendToUi[prefs.default_horizon])
}
 console.log('📋 Loaded user preferences:', prefs.daily_focus_limit,'tasks/day,', prefs.daily_hold_limit,'holds/day')
 }
 } catch (err) {
 console.warn('Could not load preferences, using defaults:', err)
 }
 }
 
// Fetch proposed (unsaved) AI suggestions from database.
//
// This is the ONLY way a hold from an EARLIER session reaches this page.
// It depends on the reader behind /api/schedule/entries answering
// ?status=proposed, which it now does: the reader takes an explicit
// include_proposed opt-in, so the ?status= filter finally runs over a
// list the holds are still in, and every row arrives with its own id,
// its §3 commitment pair and its source item.
//
// Which makes the mapping below load-bearing rather than aspirational,
// and is why it keys off the row's OWN source. Keying off parent_task_id
// — as this did — threw away every habit, skill and roadblock hold the
// reader returns, because those rows leave that field null. A thrown-away
// hold has no card, and a hold with no card has no verb: yanamas holds
// the time and the user has no way to see it, move it or let it go.
 const fetchProposedSuggestions = async () => {
 try {
 const res = await fetch('/api/schedule/entries?status=proposed', { credentials:'include' })
 if (res.ok) {
 const data = await res.json()
 if (data.success && data.entries?.length > 0) {
 // Separate hold blocks from confirmed-style proposed items
 const holdEntries = data.entries.filter(
 (e: any) => e.block_status ==='proposed' && e.block_type ==='hold'
 )
 const taskEntries = data.entries.filter(
 (e: any) => e.block_status ==='proposed' && e.block_type ==='task'
 )

 // Load existing hold blocks into holdBlocks state so their
 // tasks are excluded from"items to schedule"
 if (holdEntries.length > 0) {
 const existingHolds: HoldBlock[] = holdEntries
 .map((entry: any) => {
// Who this hold is FOR, down the same ladder the server resolves it
// with (source_item_* → recurring_source_* → parent_action_item_id →
// parent_task_id). A row with no resolvable owner is dropped below
// rather than filed under `task:undefined`, which would pile every
// orphan onto one card.
 const source = holdSourceOf(entry)
 if (!source) return null
 let startTime ='09:00'
 let endTime ='10:00'
 if (entry.time_slot) {
 const parts = entry.time_slot.split('-')
 startTime = parts[0]?.trim() ||'09:00'
 if (parts[1]) {
 endTime = parts[1].trim()
 } else if (entry.duration) {
 const [h, m] = startTime.split(':').map(Number)
 const totalMins = h * 60 + m + (entry.duration || 30)
 endTime =`${Math.floor(totalMins / 60).toString().padStart(2,'0')}:${(totalMins % 60).toString().padStart(2,'0')}`
 }
 }
// A stored row's own id. An occurrence expanded from a recurring
// master is VIRTUAL — its `id` is the synthetic `<master>_v_<date>`,
// which is deliberately left unaddressable rather than resolved to
// the master: skipping that day would be right, but CONFIRMING the
// master confirms the whole series, and no per-day control may do
// that. Those holds render, and their per-block verbs are withheld.
 const rowId = holdRowIdOf(entry)
 return {
 id: String(entry.id || entry._id),
 task_id: source.itemId,
 item_type: source.itemType as ItemToSchedule['type'],
 title: entry.activity || entry.title,
 start: startTime,
 end: endTime,
 date_key: entry.date,
 block_type:'hold' as const,
 status:'proposed' as const,
 source: entry.block_source ||'ai_assist',
 duration_minutes: entry.duration || 30,
 user_modified: entry.user_modified || false,
 origin:'schedule_store' as const,
 // The stored §3 pair wins now that the reader projects it; the
 // block_status derivation stays as the answer for a row that
 // predates the stamp and a server that has not shipped it.
 commitment: (entry.commitment_state as CommitmentState)
 ?? commitmentOfBlockStatus(entry.block_status),
 commitment_source: (entry.commitment_source ??'ai_proposal') as CommitmentSource,
 block_id: rowId,
 is_recurring_master: entry.is_recurring_master === true,
 }
 })
 .filter((h: HoldBlock | null): h is HoldBlock => h !== null)
 console.log(`📋 Loaded ${existingHolds.length} existing HOLD blocks from database`)
 setHoldBlocks(existingHolds)
 }

 // Convert proposed task entries to scheduled_items format
 const proposedItems: ScheduledItem[] = taskEntries
 .map((entry: any) => ({
 item_id: entry.parent_task_id || entry.parent_action_item_id,
 item_type: entry.parent_task_id ?'task' :'action_item',
 title: entry.activity,
 suggested_date: entry.date,
 suggested_time: entry.time_slot?.split('-')[0] ||'09:00',
 duration_minutes: entry.duration || 30,
 rationale:'Previously suggested - click Apply to confirm',
 priority_score: 5,
 grouped_with: [],
 is_recurring: entry.is_recurring_master || false,
 recurrence: null,
 accepted: true
 }))
 
 if (proposedItems.length > 0 || holdEntries.length > 0) {
 if (proposedItems.length > 0) {
 console.log(`📋 Loaded ${proposedItems.length} previously suggested items`)
 setSuggestions({
 scheduled_items: proposedItems,
 optimization_notes:'',
 warnings: [],
 unscheduled_items: []
 })
 }
 return true // Has proposed suggestions or holds
 }
 }
 }
 } catch (err) {
 console.error('Error fetching proposed suggestions:', err)
 }
 return false
 }

 const fetchInitialData = async () => {
 try {
 setLoading(true)
 
 // Fetch in parallel: items + existing schedule + user preferences
 await Promise.all([
 fetchPendingItemsOnly(),
 fetchExistingSchedule(),
 fetchUserPreferences()
 ])
 
 // Check for existing proposed suggestions (saved from previous AI Assist session)
 const hasProposed = await fetchProposedSuggestions()

 if (!hasProposed) {
 // Initialize with empty suggestions
 setSuggestions({
 scheduled_items: [],
 optimization_notes:'',
 warnings: [],
 unscheduled_items: []
 })
 }
 
 } catch (err) {
 console.error('Error fetching initial data:', err)
 setError('Failed to load schedule data')
 } finally {
 setLoading(false)
 }
 }
 
 // No auto-plan on mount: let user choose AI Assist or manual scheduling.
 // The old auto-plan would bluntly schedule holds without context.
 // User can click"Re-Plan" or"AI Assist" explicitly when ready.
 
 // Fetch hierarchy context for items (goals, aspirations, dreams)
 const fetchHierarchyContext = async (goalIds: number[]): Promise<Map<number, HierarchyContext>> => {
 const contextMap = new Map<number, HierarchyContext>()
 
 if (goalIds.length === 0) return contextMap
 
 try {
 // Fetch goals with their parent chains
 const uniqueGoalIds = [...new Set(goalIds.filter(id => id))]
 
 // Batch fetch goal details
 const goalPromises = uniqueGoalIds.map(async (goalId) => {
 try {
 const res = await fetch(`/api/hierarchy/goals/${goalId}`, { credentials:'include' })
 if (res.ok) {
 const goalData = await res.json()
 const goal = goalData.data || goalData
 
 const context: HierarchyContext = {
 goal: goal.title,
 goal_id: goal.id,
 goal_priority: goal.priority,
 goal_target_date: goal.target_date,
 }
 
 // If goal has parent aspiration
 if (goal.parent_aspiration_id) {
 try {
 const aspRes = await fetch(`/api/hierarchy/aspirations/${goal.parent_aspiration_id}`, { credentials:'include' })
 if (aspRes.ok) {
 const aspData = await aspRes.json()
 const asp = aspData.data || aspData
 context.aspiration = asp.title
 context.aspiration_id = asp.id
 
 // If aspiration has parent dream
 if (asp.parent_dream_id) {
 try {
 const dreamRes = await fetch(`/api/hierarchy/dreams/${asp.parent_dream_id}`, { credentials:'include' })
 if (dreamRes.ok) {
 const dreamData = await dreamRes.json()
 const dream = dreamData.data || dreamData
 context.dream = dream.title
 context.dream_id = dream.id
 }
 } catch { /* ignore */ }
 }
 }
 } catch { /* ignore */ }
 }
 
 // If goal has direct parent dream (no aspiration)
 if (!context.aspiration && goal.parent_dream_id) {
 try {
 const dreamRes = await fetch(`/api/hierarchy/dreams/${goal.parent_dream_id}`, { credentials:'include' })
 if (dreamRes.ok) {
 const dreamData = await dreamRes.json()
 const dream = dreamData.data || dreamData
 context.dream = dream.title
 context.dream_id = dream.id
 }
 } catch { /* ignore */ }
 }
 
 return { goalId, context }
 }
 } catch { /* ignore */ }
 return null
 })
 
 const results = await Promise.all(goalPromises)
 results.forEach(result => {
 if (result) {
 contextMap.set(result.goalId, result.context)
 }
 })
 
 } catch (err) {
 console.error('Error fetching hierarchy context:', err)
 }
 
 return contextMap
 }
 
 // Fetch all schedulable items: tasks, habits, skills, roadblocks
 const fetchPendingItemsOnly = async () => {
 try {
 const [tasksRes, habitsRes, skillsRes, roadblocksRes] = await Promise.all([
   fetch('/api/hierarchy/tasks', { credentials: 'include' }),
   fetch('/api/hierarchy/habits', { credentials: 'include' }),
   fetch('/api/hierarchy/skills', { credentials: 'include' }),
   fetch('/api/hierarchy/roadblocks', { credentials: 'include' }),
 ])
 const [tasksData, habitsData, skillsData, roadblocksData] = await Promise.all([
   tasksRes.json(),
   habitsRes.json(),
   skillsRes.json(),
   roadblocksRes.json(),
 ])
 
 const allItems: ItemToSchedule[] = []
 
 // ── Tasks ──
 const tasks = Array.isArray(tasksData) ? tasksData : (tasksData.tasks || tasksData.data || [])
 const pendingTasks = tasks.filter((t: any) => {
 const isNotCompleted = t.status !=='completed'
 const isNotScheduled = t.status !=='scheduled'
 return isNotCompleted && isNotScheduled
 })
 
 const goalIds = pendingTasks
 .map((t: any) => t.parent_goal_id)
 .filter((id: any) => id)
 
 const hierarchyContextMap = await fetchHierarchyContext(goalIds)
 
 pendingTasks.forEach((t: any) => {
 const schedulingHint: SchedulingHint | undefined = t.scheduling_hint ? {
 is_recurring: t.scheduling_hint.is_recurring || false,
 rrule: t.scheduling_hint.rrule || null,
 suggested_time_of_day: t.scheduling_hint.suggested_time_of_day || null,
 ideal_days: t.scheduling_hint.ideal_days || null,
 rationale: t.scheduling_hint.rationale || null
 } : undefined
 
 const duration = t.estimated_duration || t.duration_minutes || 30
 const hierarchyContext = t.parent_goal_id ? hierarchyContextMap.get(t.parent_goal_id) : undefined
 
allItems.push({
id: t.id,
type:'task',
title: t.title,
description: t.description,
priority: t.priority ||'medium',
estimated_duration: duration,
due_date: t.due_date,
parent_task_id: undefined,
parent_task_title: undefined,
parent_goal_id: t.parent_goal_id,
parent_goal_title: t.parent_goal_title || hierarchyContext?.goal,
parent_skill_id: t.parent_skill_id,
parent_skill_title: t.parent_skill_title,
is_skill_journey_task: !!t.skill_journey_plan_id,
hierarchy_context: hierarchyContext,
scheduling_hint: schedulingHint,
is_recurring: t.is_recurring,
recurrence_pattern: t.recurrence_pattern,
recurrence_rule: t.recurrence_rule,
ai_suggested_recurrence: t.ai_suggested_recurrence
})
 })

 // ── Habits ── (suppress formation_journey habits with an active journey task)
 const habits = Array.isArray(habitsData) ? habitsData : (habitsData.habits || habitsData.data || [])
 habits
   .filter((h: any) =>
     h.status !== 'completed' && h.status !== 'archived' && h.status !== 'abandoned'
     && !(h.habit_execution_mode === 'formation_journey' && h.active_journey_task_id)
   )
   .forEach((h: any) => {
     allItems.push({
       id: h.id,
       type: 'habit',
       title: h.title,
       description: h.description,
       priority: h.priority || 'medium',
       estimated_duration: h.duration_minutes || h.estimated_duration || 30,
       parent_goal_id: h.parent_goal_id,
       parent_goal_title: h.parent_goal_title,
       is_recurring: true,
       recurrence_pattern: h.recurrence_pattern || h.target_cadence || 'daily',
       recurrence_rule: h.recurrence_rule || h.rrule,
       preferred_start_time: h.preferred_start_time,
       preferred_time_window: h.preferred_time_window,
       preferred_days: Array.isArray(h.preferred_days) ? h.preferred_days : undefined,
       duration_minutes: typeof h.duration_minutes === 'number' ? h.duration_minutes : undefined,
     })
   })

// ── Skills ── (suppress skills with an active journey task)
const skills = Array.isArray(skillsData) ? skillsData : (skillsData.skills || skillsData.data || [])
skills
  .filter((s: any) =>
    s.status !== 'completed' && s.status !== 'archived' && s.status !== 'abandoned'
    && !s.active_journey_task_id
  )
  .forEach((s: any) => {
     allItems.push({
       id: s.id,
       type: 'skill',
       title: s.title,
       description: s.description,
       priority: s.priority || 'medium',
       estimated_duration: s.estimated_duration || 45,
       parent_goal_id: s.parent_goal_id,
       parent_goal_title: s.parent_goal_title,
       is_recurring: true,
       recurrence_pattern: 'weekly',
     })
   })

 // ── Roadblocks ──
 const roadblocks = Array.isArray(roadblocksData) ? roadblocksData : (roadblocksData.roadblocks || roadblocksData.data || [])
 roadblocks
   .filter((r: any) => r.status !== 'resolved' && r.status !== 'archived' && r.status !== 'abandoned')
   .forEach((r: any) => {
     allItems.push({
       id: r.id,
       type: 'roadblock',
       title: r.title,
       description: r.description,
       priority: r.priority || r.severity_label || 'medium',
       estimated_duration: r.estimated_duration || 30,
       parent_goal_id: r.parent_goal_id,
       parent_goal_title: r.parent_goal_title,
       is_recurring: false,
     })
   })
 
 setItems(allItems)
 } catch (err) {
 console.error('Error fetching schedulable items:', err)
 setItems([])
 }
 }
 
 
 // Drag and drop handlers
 const handleDragStart = (e: DragEvent<HTMLDivElement>, item: ItemToSchedule) => {
 setDraggedItem(item)
 e.dataTransfer.effectAllowed ='move'
 e.dataTransfer.setData('text/plain', item.id.toString())
 }
 
 const handleDragEnd = () => {
 setDraggedItem(null)
 setDropTargetHour(null)
 }
 
 const handleDragOver = (e: DragEvent<HTMLDivElement>, hour: number) => {
 e.preventDefault()
 e.dataTransfer.dropEffect ='move'
 setDropTargetHour(hour)
 }
 
 const handleDragLeave = () => {
 setDropTargetHour(null)
 }
 
 const handleDrop = (e: DragEvent<HTMLDivElement>, hour: number) => {
 e.preventDefault()
 setDropTargetHour(null)
 
 if (!draggedItem) return
 
 const time =`${hour.toString().padStart(2,'0')}:00`
 scheduleItemWithRecurrence(draggedItem, format(displayDate,'yyyy-MM-dd'), time, draggedItem.estimated_duration, null)
 setDraggedItem(null)
 }
 
 // Remove scheduled item
 const removeScheduledItem = (itemId: number) => {
 setSuggestions(prev => {
 if (!prev) return prev
 return {
 ...prev,
 scheduled_items: prev.scheduled_items.filter(s => s.item_id !== itemId)
 }
 })
 }
 
 // Apply schedule
 const applySchedule = async () => {
 if (!suggestions) return
 
 const acceptedItems = suggestions.scheduled_items.filter(item => item.accepted)
 
 if (acceptedItems.length === 0) {
 setError('Please schedule at least one item')
 return
 }
 
 try {
 setApplying(true)
 
 const response = await fetch('/api/ai-scheduler/apply', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify({
 scheduled_items: acceptedItems.map(item => ({
 item_id: item.item_id,
 item_type: item.item_type,
 suggested_date: item.suggested_date,
 suggested_time: item.suggested_time,
 duration_minutes: item.duration_minutes,
 is_recurring: item.is_recurring || false,
 recurrence: item.recurrence || null
 }))
 })
 })
 
 const data = await response.json()
 
 if (data.success) {
 navigate('/schedule', { 
 state: { message:`Successfully scheduled ${data.updated.length} items!` } 
 })
 } else {
 setError(data.error ||'Failed to apply schedule')
 }
 } catch (err) {
 console.error('Error applying schedule:', err)
 setError('Failed to apply schedule')
 } finally {
 setApplying(false)
 }
 }
 
// `commitSuggestionsAsHolds` was removed (2026-04) along with the AI
// HOLD/CONFIRM modal and has not come back: holds are now written by
// `/create-blocks` at suggestion time (see `aiScheduleRemaining`), not
// committed as a second step. `applySchedule` above stays the commit for
// placements the USER made by hand.

// Quick schedule handlers - pre-populate from scheduling_hint if available
 const openQuickSchedule = (item: ItemToSchedule) => {
 setQuickScheduleItem(item)
 setQuickScheduleDate(format(displayDate,'yyyy-MM-dd'))
 setQuickScheduleDuration(item.estimated_duration || 30)
 
 // If item has a scheduling_hint from Planner LLM, use it to pre-populate
 if (item.scheduling_hint) {
 const hint = item.scheduling_hint
 
 // Set suggested time based on time_of_day hint
 setQuickScheduleTime(getDefaultTimeFromHint(hint.suggested_time_of_day))
 
 // Parse RRULE if present
 if (hint.rrule) {
 const { pattern, days } = parseRrule(hint.rrule)
 setQuickScheduleRecurrence(pattern)
 setQuickScheduleCustomDays(days)
 } else if (hint.is_recurring) {
 // Fallback: if is_recurring but no RRULE, default to daily
 setQuickScheduleRecurrence('daily')
 setQuickScheduleCustomDays([])
 } else {
 setQuickScheduleRecurrence(null)
 setQuickScheduleCustomDays([])
 }
 } else {
 // No scheduling hint - use defaults
 setQuickScheduleTime('09:00')
 setQuickScheduleRecurrence(null)
 setQuickScheduleCustomDays([])
 }
 
 setQuickScheduleEndDate('')
 }
 
 const confirmQuickSchedule = () => {
 if (!quickScheduleItem) return
 
 // Build recurrence info if needed
 let recurrenceInfo: RecurrenceInfo | null = null
 if (quickScheduleRecurrence) {
 recurrenceInfo = {
 pattern: quickScheduleRecurrence as RecurrenceInfo['pattern'],
 preferred_time: quickScheduleTime,
 days_of_week: quickScheduleRecurrence ==='custom' ? quickScheduleCustomDays : undefined,
 end_date: quickScheduleEndDate || undefined
 }
 }
 
 scheduleItemWithRecurrence(
 quickScheduleItem, 
 quickScheduleDate, 
 quickScheduleTime, 
 quickScheduleDuration,
 recurrenceInfo
 )
 setQuickScheduleItem(null)
 }
 
 // Schedule item with full recurrence info
 const scheduleItemWithRecurrence = (
 item: ItemToSchedule, 
 date: string, 
 time: string, 
 duration: number, 
 recurrence: RecurrenceInfo | null
 ) => {
 const newScheduledItem: ScheduledItem = {
 item_id: item.id,
 item_type: item.type,
 title: item.title,
 suggested_date: date,
 suggested_time: time,
 duration_minutes: duration,
 rationale:'Manually scheduled',
 priority_score: item.priority ==='urgent' ? 10 : item.priority ==='high' ? 8 : item.priority ==='medium' ? 5 : 3,
 grouped_with: [],
 accepted: true,
 modified: true,
 is_recurring: !!recurrence,
 recurrence: recurrence
 }
 
 setSuggestions(prev => {
 if (!prev) {
 return {
 scheduled_items: [newScheduledItem],
 optimization_notes:'',
 warnings: [],
 unscheduled_items: []
 }
 }
 
 const unscheduled = prev.unscheduled_items.filter(u => u.item_id !== item.id)
 const existingScheduled = prev.scheduled_items.filter(s => s.item_id !== item.id)
 
 return {
 ...prev,
 scheduled_items: [...existingScheduled, newScheduledItem],
 unscheduled_items: unscheduled
 }
 })
 }
 
 // Toggle custom day selection
 const toggleCustomDay = (dayValue: number) => {
 setQuickScheduleCustomDays(prev => 
 prev.includes(dayValue) 
 ? prev.filter(d => d !== dayValue)
 : [...prev, dayValue].sort()
 )
 }
 
// AI Assist entry point. There is no HOLD-vs-CONFIRM question to ask: the
// product rule is fixed by the constitution, not chosen per click.
const handleAiAssistClick = () => {
if (itemsToSchedule.length === 0) {
setError('All items are already scheduled!')
return
}
aiScheduleRemaining()
}

// AI Schedule remaining items \u2014 HOLD-first.
//
// Ratified decision #1: Horton-assisted scheduling produces FLEXIBLE
// holds; the user confirms whichever of them must not move. This reverses
// the 2026 "anything scheduled here is confirmed" rule, whose removal of HOLD-first
// was a reaction to approval friction \u2014 answered here by SC-6 (zero-nag),
// not by making Horton's guesses into the user's commitments. An AI
// suggestion is not a user obligation (SC-35), so the rows this writes
// stay `flexible` / `ai_proposal` until the user says otherwise.
//
// The mode is what makes the rest of this lane reachable: it is what
// makes the server write `block_type='hold'`, and `find_holds` \u2014 the
// query behind /confirm-holds \u2014 matches nothing else.
const aiScheduleRemaining = async () => {
// Only schedule items that have NO existing schedule state
// (not on hold, not in current suggestions, not already confirmed/on calendar)
const remainingItems = itemsToSchedule

if (remainingItems.length === 0) {
setError('All items are already scheduled!')
return
}

try {
setAiSchedulingRemaining(true)
setError(null)
 
 // Build context of already scheduled items for AI
 const alreadyScheduled = suggestions?.scheduled_items.map(s => ({
 title: s.title,
 date: s.suggested_date,
 time: s.suggested_time,
 duration: s.duration_minutes
 })) || []
 
 const controller = new AbortController()
 const timeoutId = setTimeout(() => controller.abort(), 45000)
 
 // Step 1: Get AI suggestions
 const response = await fetch('/api/ai-scheduler/suggest', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 signal: controller.signal,
 body: JSON.stringify({
 items: remainingItems.map(item => ({
 id: item.id,
 type: item.type,
 title: item.title,
 description: item.description,
 priority: item.priority,
 estimated_duration: item.estimated_duration,
 due_date: item.due_date,
 // Include full hierarchy context for AI to understand the bigger picture
 hierarchy_context: item.hierarchy_context ? {
 dream: item.hierarchy_context.dream,
 aspiration: item.hierarchy_context.aspiration,
 goal: item.hierarchy_context.goal,
 goal_priority: item.hierarchy_context.goal_priority,
 goal_target_date: item.hierarchy_context.goal_target_date,
 parent_task: item.hierarchy_context.parent_task,
 } : undefined,
 // CRITICAL: Include scheduling preferences for AI to respect user's intent
is_recurring: item.is_recurring,
recurrence_pattern: item.recurrence_pattern,
recurrence_rule: item.recurrence_rule,
ai_suggested_recurrence: item.ai_suggested_recurrence,
preferred_start_time: item.preferred_start_time,
preferred_time_window: item.preferred_time_window,
preferred_days: item.preferred_days,
duration_minutes: item.duration_minutes,
})),
preferences: {
work_start: workStart,
work_end: workEnd,
focus_preference: focusPreference,
days_ahead: 7,
// Pass orchestrator limits so AI respects them
 daily_focus_limit: userPrefs.daily_focus_limit,
 daily_hold_limit: userPrefs.daily_hold_limit,
 max_parallel_tasks: userPrefs.max_parallel_tasks
 },
 ai_schedule_mode:'hold',
 // Send already scheduled items as context
 already_scheduled: alreadyScheduled,
 existing_calendar_events: calendarEvents,
 // Send existing holds so AI doesn't duplicate them
 existing_holds: effectiveHoldBlocks.map(h => ({
 task_id: h.task_id,
 date: h.date_key,
 time: holdStartTime(h)
 }))
 })
 })
 
 clearTimeout(timeoutId)
 
 const data = await response.json()
 
 if (data.success && data.suggestions?.scheduled_items) {
 // Deduplicate: LLM may return multiple entries for the same item_id on
 // different days (e.g. a recurring task expanded into 3 occurrences).
 // Keep only the earliest occurrence per item_id; the recurrence metadata
 // on that entry already captures the pattern.
 const deduped: typeof data.suggestions.scheduled_items = []
 const seenIds = new Set<number>()
 const sorted = [...data.suggestions.scheduled_items].sort(
 (a: any, b: any) => (a.suggested_date ||'').localeCompare(b.suggested_date ||'')
 )
 for (const item of sorted) {
 if (!seenIds.has(item.item_id)) {
 seenIds.add(item.item_id)
 deduped.push(item)
 }
 }
 if (deduped.length < data.suggestions.scheduled_items.length) {
 console.log(
`AI dedup: collapsed ${data.suggestions.scheduled_items.length} entries → ${deduped.length} unique items`
 )
 }

// Write the plan as Flexible holds. These rows are Horton's plan, not
// the user's commitment: they carry `flexible` / `ai_proposal` and stay
// that way until a verb below is used, or forever (SC-6).
const blocksResult = await createFlexibleBlocks(deduped)

 if (blocksResult.ok) {
 const durationOf = (slot: string, fallback: number) => {
 const [from, to] = slot.split('-')
 if (!from || !to) return fallback
 const [fh, fm] = from.trim().split(':').map(Number)
 const [th, tm] = to.trim().split(':').map(Number)
 const mins = (th * 60 + tm) - (fh * 60 + fm)
 return Number.isFinite(mins) && mins > 0 ? mins : fallback
 }

 const newHolds: HoldBlock[] = blocksResult.created.map((block) => {
 const startTime = (block.time_slot ||'09:00').split('-')[0].trim()
 const duration = durationOf(block.time_slot ||'', block.duration_minutes || 30)
 return {
 // The local key stays coordinate-derived: it must be stable across
 // re-plans of the same item on the same day so a second pass
 // replaces the card rather than duplicating it. `block_id` below
 // is the row's real address, which is a different question.
 id:`ai-hold-${block.item_type}-${block.item_id}-${block.date}`,
 task_id: String(block.item_id),
 item_type: block.item_type as ItemToSchedule['type'],
 title: block.title,
 start:`${block.date}T${startTime}:00`,
 end:`${block.date}T${calculateEndTime(startTime, duration)}:00`,
 date_key: block.date,
 block_type:'hold' as const,
 status:'proposed' as const,
 source:'ai_assist' as const,
 duration_minutes: duration,
 user_modified: false,
 origin:'schedule_store' as const,
 commitment:'flexible' as const,
 commitment_source:'ai_proposal' as const,
 // /create-blocks now answers with the row it wrote. That id is
 // what makes Confirm-all exact and Remove real: without it the
 // page could only confirm a date span (sweeping in rows it never
 // showed) and could only hide a card while its row stayed live.
 block_id: block.id ?? null,
 // ...and what that id addresses. A recurring habit/skill/roadblock
 // is written as a MASTER, so this id is the rule, not the day —
 // Confirm on it commits every occurrence and Remove releases only
 // one. Carrying the flag is what lets the verbs say which.
 is_recurring_master: block.is_recurring_master === true,
 }
 })

 setHoldBlocks(prev => {
 const fresh = new Set(newHolds.map(h => h.id))
 return [...prev.filter(h => !fresh.has(h.id)), ...newHolds]
 })

 setSuggestions(prev => ({
 scheduled_items: prev?.scheduled_items || [],
 optimization_notes: data.suggestions.optimization_notes ||'',
 warnings: data.suggestions.warnings || [],
 unscheduled_items: data.suggestions.unscheduled_items || []
 }))

 if (!blocksResult.applied) {
 setHoldNotice(blocksResult.message ||'Saved — not on the calendar yet.')
 } else if (blocksResult.rejected.length > 0) {
 // SC-21: a shorter plan is a success, not a shortfall. Say what
 // stayed off the calendar; never invite the user to fill the gap.
 setHoldNotice(`${newHolds.length} time${newHolds.length === 1 ?'' :'s'} held. ${blocksResult.rejected.length} left unplaced — those days were already full.`)
 }

} else {
setError(blocksResult.error ||'Could not save the plan')
}

// Update free slots if provided
if (data.free_slots) {
setFreeSlots(data.free_slots)
}
} else {
setError(data.error ||'AI could not schedule remaining items')
}
} catch (err: any) {
console.error('Error in AI scheduling:', err)
if (err.name ==='AbortError') {
setError('AI scheduling timed out. Please try again.')
} else {
setError('Failed to connect to AI scheduler')
}
} finally {
setAiSchedulingRemaining(false)
}
}

// The 🔓/🔒 affordance IS the Flexible/Confirmed switch (§3). Confirming
// is the user accepting yanamas's placement; the other direction hands the
// block back so yanamas may keep optimizing it (SC-5) — never a demotion,
// never a thing the user is asked to justify.
//
// Item-scoped, so it carries the same reach limit as every other
// item-scoped write here: it resolves an item id through `parent_task_id`
// and would otherwise land on whatever task shares the number.
 const toggleItemLockStatus = async (
 itemId: number,
 itemType: string,
 currentStatus:'confirmed' |'proposed',
 ) => {
 const newStatus = currentStatus ==='confirmed' ?'proposed' :'confirmed'
 const target: CommitmentState = newStatus ==='confirmed' ?'confirmed' :'flexible'

 if (!canSetItemCommitment(itemType)) {
 setHoldNotice('Could not reach that one from here — nothing changed.')
 return
 }

 // Optimistic: the switch must feel instant. Rolled back below if the
 // write did not land, so the chip never lies about the stored state.
 setSuggestions(prev => {
 if (!prev) return prev
 return {
 ...prev,
 scheduled_items: prev.scheduled_items.map(item =>
 item.item_id === itemId ? { ...item, block_status: newStatus } : item
 )
 }
 })

 const result = await setItemCommitment(itemId, target, itemType)
 if (result.ok && result.applied) return

 setSuggestions(prev => {
 if (!prev) return prev
 return {
 ...prev,
 scheduled_items: prev.scheduled_items.map(item =>
 item.item_id === itemId ? { ...item, block_status: currentStatus } : item
 )
 }
 })
 if (!result.ok) {
 setError(result.error ||'Could not change that block')
 } else {
 setHoldNotice(result.message ||'Saved — not on the calendar yet.')
 }
 }
 
 // Preview which items will be affected by rebalance
 const fetchRebalancePreview = async () => {
 try {
 setLoadingPreview(true)
 
 const response = await fetch('/api/ai-scheduler/rebalance-preview', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify({
 scope: rebalanceScope,
 include_today: rebalanceIncludeToday
 })
 })
 
 const data = await response.json()
 
 if (data.success) {
 setRebalancePreview(data.blocks_to_rebalance || [])
 setRebalanceStep('preview')
 } else {
 setError(data.error ||'Failed to fetch preview')
 }
 } catch (err) {
 console.error('Error fetching preview:', err)
 setError('Failed to fetch preview')
 } finally {
 setLoadingPreview(false)
 }
 }
 
 // Rebalance: Convert CONFIRMED blocks back to HOLD for re-optimization
 const handleRebalance = async () => {
 try {
 setRebalancing(true)
 setError(null)
 
 const response = await fetch('/api/ai-scheduler/rebalance', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify({
 scope: rebalanceScope,
 include_today: rebalanceIncludeToday
 })
 })
 
 const data = await response.json()
 
 if (data.success) {
 // Convert rebalanced blocks to HoldBlocks for UI
 const newHoldBlocks: HoldBlock[] = data.rebalanced_blocks.map((block: any) => ({
 id:`hold_rebalanced_${block.id}`,
 task_id: String(block.id),
 item_type:'task' as const,
 title: block.title,
 start:`${block.date}T${block.time_slot.split('-')[0]}:00`,
 end:`${block.date}T${block.time_slot.split('-')[1]}:00`,
 date_key: block.date,
 block_type:'hold' as const,
 status:'proposed' as const,
 source:'horton_rebalanced' as const,
 duration_minutes: 30, // Default, ideally get from backend
 user_modified: false,
 origin:'schedule_store' as const,
 commitment:'flexible' as const,
 commitment_source:'ai_proposal' as const,
 block_id: Number(block.id)
 }))
 
 setHoldBlocks(prev => [...prev, ...newHoldBlocks])
 
 // Update local suggestions to mark items as'proposed'
 const rebalancedIds = new Set(data.rebalanced_blocks.map((b: any) => b.id))
 setSuggestions(prev => {
 if (!prev) return prev
 return {
 ...prev,
 scheduled_items: prev.scheduled_items.map(item => 
 rebalancedIds.has(item.item_id) ? { ...item, block_status:'proposed' as const } : item
 )
 }
 })
 
 // Close modal and reset
 setShowRebalanceModal(false)
 setRebalanceStep('options')
 setRebalancePreview([])
 
 // Show success message
 console.log(`Rebalanced ${data.rebalanced_count} blocks. ${data.preserved_count} user-modified blocks preserved.`)
 } else {
 setError(data.error ||'Failed to rebalance schedule')
 }
 } catch (err: any) {
 console.error('Error rebalancing:', err)
 setError('Failed to connect to scheduler')
 } finally {
 setRebalancing(false)
 }
 }
 
 // Calculate position and height for scheduled items on timeline
 const getTimelinePosition = (time: string, duration: number) => {
 const [hours, minutes] = time.split(':').map(Number)
 // Offset from midnight (row 0 = 12am). 60px per hour.
 const startMinutes = hours * 60 + minutes
 const top = (startMinutes / 60) * 60
 const height = (duration / 60) * 60
 return { top, height: Math.max(height, 20) }
 }
 
 if (loading) {
 return (
 <div className="min-h-screen bg-default flex items-center justify-center">
 <div className="text-center">
 <Sparkles className="w-16 h-16 text-brand animate-pulse mx-auto mb-4" />
 <h2 className="text-xl font-semibold text-primary mb-2">
 Loading your items...
 </h2>
 <p className="text-secondary">
 Preparing the scheduler
 </p>
 </div>
 </div>
 )
 }
 
 if (items.length === 0) {
 return (
 <div className="min-h-screen bg-default flex items-center justify-center p-6">
 <Card className="max-w-md w-full p-8 text-center">
 <CalendarDays className="w-16 h-16 text-muted mx-auto mb-4" />
 <h2 className="text-xl font-semibold text-primary mb-2">
 No Tasks to Schedule
 </h2>
 <p className="text-secondary mb-6">
 Tasks with duration estimates can be scheduled to your calendar. Chat with {BRAND.assistant} to create tasks, or make sure your tasks have duration set.
 </p>
 <div className="flex gap-3 justify-center">
 <Button variant="outline" onClick={() => navigate('/tasks')}>
 <Target className="w-4 h-4 mr-2" />
 View Tasks
 </Button>
 <Button onClick={() => navigate('/horton-ai')}>
 <Sparkles className="w-4 h-4 mr-2" />
 Chat with {BRAND.assistant}
 </Button>
 </div>
 </Card>
 </div>
 )
 }
 
 return (
 <div className="min-h-screen bg-default">
 {/* Header — two rows on desktop so the stat and helper are actually
     readable. The previous pass set child spans to `text-brand`, which
     on a `bg-brand` header renders as brand-on-brand (near-invisible).
     All stat/helper text now uses `text-on-brand` with clear weight and
     contrast. Action buttons sit on their own row with the view
     switcher visually separated from the right-aligned primary action. */}
 <header className="bg-brand text-on-brand py-3 sm:py-4 px-3 sm:px-6 sticky top-0 z-40 shadow-sm">
 <div className="max-w-[1800px] mx-auto flex flex-col gap-3">
 {/* Row 1 — title, stat, helper */}
 <div className="flex items-start sm:items-center justify-between gap-3">
 <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
 <h1 className="text-lg sm:text-xl font-semibold flex items-center gap-2 flex-shrink-0">
 <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
 <span className="hidden sm:inline">Review Schedule</span>
 <span className="sm:hidden">Schedule</span>
 </h1>
 <div className="flex flex-col min-w-0 flex-1">
{/* Phase 4 — split task vs habit counters.
    Tasks consume the daily focus budget; habits don't. Reading the
    header as three distinct numbers makes that invariant visible
    instead of hidden behind a combined "scheduled / remaining" pair. */}
<div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-on-brand text-sm sm:text-base font-semibold">
{confirmedTasksTotal > 0 && (
<span className="whitespace-nowrap">{confirmedTasksTotal} task{confirmedTasksTotal === 1 ? '' : 's'} confirmed</span>
)}
{effectiveHoldBlocks.length > 0 ? (
<>
{/* SC-6: this is a plan stat, not an inbox. It says how much time
    Horton is holding — never "N awaiting your approval". */}
<span className="whitespace-nowrap">{itemsOnHold.length} held flexibly</span>
<span className="whitespace-nowrap">{itemsToScheduleTasks.length} task{itemsToScheduleTasks.length === 1 ? '' : 's'} to plan</span>
</>
) : (
<>
<span className="whitespace-nowrap">{itemsScheduledTasks.length} task{itemsScheduledTasks.length === 1 ? '' : 's'} scheduled</span>
<span className="whitespace-nowrap">{itemsToScheduleTasks.length} slot{itemsToScheduleTasks.length === 1 ? '' : 's'} remaining</span>
</>
)}
{(itemsScheduledHabits.length + confirmedHabitsTotal) > 0 && (
<span
className="whitespace-nowrap opacity-90"
title="Habits are recurrence-driven and don't consume the daily focus budget."
>
{itemsScheduledHabits.length + confirmedHabitsTotal} habit{(itemsScheduledHabits.length + confirmedHabitsTotal) === 1 ? '' : 's'} placed
<span className="opacity-75"> (not counted)</span>
</span>
)}
<span className="px-2 py-0.5 bg-on-brand/15 border border-on-brand/25 rounded-full text-xs font-medium whitespace-nowrap">
{userPrefs.daily_focus_limit}/day
</span>
</div>
{/* Helper text — softer but still clearly legible.
    Hold-first copy grants permission to optimize; it does not ask for
    approval. "Review these and confirm" is exactly the chore SC-6
    forbids, and the friction that killed HOLD-first the first time. */}
<p className="hidden sm:block text-on-brand/80 text-xs sm:text-sm mt-0.5 truncate">
{effectiveHoldBlocks.length > 0
?`${BRAND.assistant} is holding these times and can still shuffle them. Confirm anything that must not move.`
:'Drag items to time slots or use AI to schedule. Habits don\u2019t eat into the task budget.'}
</p>
 </div>
 </div>
 </div>
 {/* Row 2 — action bar. Horizon switcher left, action buttons right. */}
 <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0 sm:justify-between">
{/* Horizon / view switcher — Day / Week / Month / Year. Replaces the
    old dropdown. The user's mental model ("plan the day", "plan the
    week") reads more cleanly as a segmented control, and it also makes
    Re-Plan's scope self-evident: whatever is highlighted here is what
    gets replanned when Re-Plan is clicked. See `planHorizon` for the
    Horizon → horizon_days mapping. */}
<div
role="tablist"
aria-label="Planning scope"
className="flex items-center rounded-lg bg-on-brand/10 border border-on-brand/30 p-0.5 flex-shrink-0"
>
{(['day', 'week', 'month', 'year'] as const).map((opt) => {
const active = selectedHorizon === opt
return (
<button
key={opt}
type="button"
role="tab"
aria-selected={active}
onClick={() => setSelectedHorizon(opt)}
className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm capitalize transition-colors ${
active
? 'bg-surface text-brand font-semibold shadow-sm'
: 'text-on-brand/90 hover:bg-on-brand/10'
}`}
>
{opt}
</button>
)
})}
</div>

{/* Action-button cluster — grouped on the right so the primary
    "Apply/Accept" has visual weight separate from the secondary
    plan/AI/clear actions. A thin vertical rule marks the split
    between Re-Plan/AI (generate) and Clear/Apply (commit). */}
<div className="flex items-center gap-2 flex-shrink-0">
{/* Re-Plan Button — scope is whatever the switcher above is on.
    Label reflects the scope so there's no "did I just blow up my whole
    year of plans?" surprise. */}
<Button
variant="outline"
className="bg-on-brand/10 border-on-brand/30 text-on-brand hover:bg-on-brand/20 text-xs sm:text-sm py-1.5 flex-shrink-0"
onClick={() => planHorizon(selectedHorizon)}
disabled={planningHorizon}
title={`Re-plan only the selected ${selectedHorizon}`}
>
{planningHorizon ? (
<><Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 animate-spin" /><span className="hidden sm:inline">Planning {selectedHorizon}…</span><span className="sm:hidden">Plan</span></>
) : (
<><RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" /><span className="hidden sm:inline">Re-Plan {selectedHorizon}</span><span className="sm:hidden">Plan</span></>
)}
</Button>

 {/* AI Assist Button (Optional) */}
 {items.length > effectiveHoldBlocks.length && (
 <Button
 variant="outline"
 className="bg-on-brand/10 border-on-brand/30 text-on-brand hover:bg-on-brand/20 text-xs sm:text-sm py-1.5 flex-shrink-0"
 onClick={handleAiAssistClick}
 disabled={aiSchedulingRemaining}
 title="Ask AI to suggest optimal times"
 >
 {aiSchedulingRemaining ? (
 <><Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 animate-spin" />AI</>
 ) : (
 <><Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />AI</>
 )}
 </Button>
 )}

{/* Divider between generate-actions (Re-Plan / AI) and
    commit-actions (Clear / Apply). Hidden on mobile because the bar
    is already scrollable there. */}
<span
aria-hidden="true"
className="hidden sm:block w-px h-6 bg-on-brand/25 mx-1 flex-shrink-0"
/>

{/* Rebalance removed per UX simplification (2026-04).
    'Re-Plan' (above) now covers both "plan from scratch" and "redo with
    fresh context" for the selected horizon. The rebalance modal + handler
    remain defined earlier in the file but are intentionally unreachable
    from the UI; if you're re-adding a rebalance affordance, wire it into
    the options menu rather than the primary action strip. */}

{/* Clear — discards the plan on screen AND releases the flexible times
    behind it (same day-scoped release as a card's Remove). It used to
    empty the list only, which left those rows live on the calendar with
    no card left to remove them from. The title says what it does; the
    handler reports anything it could not release. */}
{/* Locked out while a confirm is in flight: Clear releases the very
    rows Confirm all is stamping, and letting both run would decide the
    same block's fate twice. */}
 <Button
 variant="outline"
 disabled={clearingView || confirmingAll || confirmingHolds}
 className="bg-on-brand/10 border-on-brand/30 text-on-brand hover:bg-on-brand/20 text-xs sm:text-sm py-1.5 flex-shrink-0"
 title={`Discard this plan and give back the flexible times ${BRAND.assistant} is holding`}
 onClick={clearPlanningView}
 >
 {clearingView ? (
 <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 animate-spin" />
 ) : (
 <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
 )}
 <span className="hidden sm:inline">Clear</span>
 </Button>

{/* Confirm all — an OPTION, never the completion of a chore.
    Deliberately secondary styling: under HOLD-first, leaving every block
    flexible is a perfectly good day (SC-6/SC-21), so the page must not
    read as "unfinished until you press this". "All" is the cards on
    screen and nothing else: the verb names their row ids, and
    confirm-holds refuses an unscoped body, so a stray click can neither
    confirm a whole year nor reach a hold this session never showed. */}
 {flexibleHoldCount > 0 && (
 <Button
 variant="outline"
 onClick={confirmAllFlexible}
 disabled={confirmingAll || confirmingHolds || clearingView}
 className="bg-on-brand/10 border-on-brand/30 text-on-brand hover:bg-on-brand/20 text-xs sm:text-sm py-1.5 flex-shrink-0"
 title={`Confirm the flexible times ${BRAND.assistant} is holding`}
 >
 {confirmingAll || confirmingHolds ? (
 <><Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 animate-spin" /><span className="hidden sm:inline">Confirming…</span><span className="sm:hidden">…</span></>
 ) : (
 <><Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" /><span className="hidden sm:inline">Confirm all</span><span className="sm:hidden">Confirm</span></>
 )}
 </Button>
 )}

 {/* Apply — commits the times the USER placed by hand (drag / quick
     schedule). Those are user_direct placements and belong Confirmed
     (SC-7); Horton's own holds do not pass through here. */}
 <Button
 onClick={applySchedule}
 disabled={applying || !suggestions?.scheduled_items?.length}
 className="bg-surface text-brand hover:bg-brand-subtle text-xs sm:text-sm py-1.5 font-semibold flex-shrink-0"
 >
 {applying ? (
 <><Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 animate-spin" /><span className="hidden sm:inline">Applying...</span><span className="sm:hidden">...</span></>
 ) : (
 <><Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" /><span className="hidden sm:inline">Apply ({suggestions?.scheduled_items?.length || 0})</span><span className="sm:hidden">Apply ({suggestions?.scheduled_items?.length || 0})</span></>
 )}
 </Button>
 </div>
 </div>
 </div>
 </header>
 
 {/* Error Display */}
 {error && (
 <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-2 sm:py-3">
 <div className="bg-bad/10 border border-bad/30 rounded-lg p-2 sm:p-3 flex items-center gap-2 sm:gap-3">
 <AlertTriangle className="w-4 h-4 text-bad flex-shrink-0" />
 <p className="text-xs sm:text-sm text-bad min-w-0 truncate">{error}</p>
 <button onClick={() => setError(null)} className="ml-auto text-bad hover:text-bad flex-shrink-0">
 <X className="w-4 h-4" />
 </button>
 </div>
 </div>
 )}

{/* Quiet notice — outcomes that are information, not failure:
    "that day is already full, it stays flexible", "saved, not on the
    calendar yet". Never red, never a prompt to act. */}
 {holdNotice && (
 <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-2 sm:py-3">
 <div className="bg-surface-alt border border-soft rounded-lg p-2 sm:p-3 flex items-center gap-2 sm:gap-3">
 <Unlock className="w-4 h-4 text-secondary flex-shrink-0" />
 <p className="text-xs sm:text-sm text-secondary min-w-0">{holdNotice}</p>
 <button onClick={() => setHoldNotice(null)} className="ml-auto text-muted hover:text-secondary flex-shrink-0">
 <X className="w-4 h-4" />
 </button>
 </div>
 </div>
 )}

 <div className="max-w-[1800px] mx-auto px-2 sm:px-6 py-3 sm:py-4">
{/* Date Navigation — strip length follows the selected horizon:
    - day:   1 cell  (just the focused date)
    - week:  7 cells (current behaviour)
    - month: 30 cells (horizontally scrollable)
    - year:  12 monthly chips with aggregated hold/scheduled markers
    The chevrons still nudge selection by one day so fine-grained
    navigation remains possible regardless of scope. */}
<div className="flex items-center justify-center gap-1 sm:gap-2 mb-3 sm:mb-4">
<button
onClick={() => setSelectedDate(subDays(displayDate, 1))}
className="p-1.5 sm:p-2 rounded-lg hover:bg-surface-alt flex-shrink-0"
aria-label="Previous day"
>
<ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
</button>

<div className="flex gap-0.5 sm:gap-1 overflow-x-auto">
{selectedHorizon === 'year'
? Array.from({ length: 12 }, (_, i) => {
    // Month chip: show the month name and aggregated counts.
    const monthStart = addDays(startOfDay(serverDate), i * 30)
    const monthPrefix = format(monthStart, 'yyyy-MM')
    const isSelected = selectedDate && format(selectedDate, 'yyyy-MM') === monthPrefix
    const scheduledCount = suggestions?.scheduled_items.filter(
      s => s.suggested_date?.startsWith(monthPrefix)
    ).length || 0
    const holdsCount = effectiveHoldBlocks.filter(
      h => h.date_key.startsWith(monthPrefix)
    ).length
    return (
      <button
        key={monthPrefix}
        onClick={() => setSelectedDate(monthStart)}
        className={`
          px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex-shrink-0
          ${isSelected ? 'bg-brand text-on-brand shadow-lg' : 'bg-surface hover:bg-surface-alt'}
        `}
      >
        <div className="text-app-micro sm:text-xs opacity-70">{format(monthStart, 'MMM')}</div>
        <div className="text-sm sm:text-base">{format(monthStart, 'yyyy')}</div>
        {!isSelected && (holdsCount > 0 || scheduledCount > 0) && (
          <div className="flex gap-0.5 justify-center mt-0.5 sm:mt-1 text-app-nano text-muted">
            {holdsCount > 0 && <span>{holdsCount}H</span>}
            {scheduledCount > 0 && <span>{scheduledCount}S</span>}
          </div>
        )}
      </button>
    )
  })
: Array.from({
    length:
      selectedHorizon === 'day' ? 1 :
      selectedHorizon === 'month' ? 30 :
      7
  }, (_, i) => {
const date = selectedHorizon === 'day'
  ? startOfDay(selectedDate || serverDate)
  : addDays(startOfDay(serverDate), i)
const dateStr = format(date,'yyyy-MM-dd')
const isSelected = selectedDate && isSameDay(date, selectedDate)
const hasScheduled = suggestions?.scheduled_items.some(
s => s.suggested_date === dateStr
)
const hasHolds = effectiveHoldBlocks.some(h => h.date_key === dateStr)
return (
<button
key={i}
onClick={() => setSelectedDate(date)}
className={`
px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex-shrink-0
${isSelected 
?'bg-brand text-on-brand shadow-lg' 
:'bg-surface hover:bg-surface-alt'
}
`}
>
<div className="text-app-micro sm:text-xs opacity-70">{format(date,'EEE')}</div>
<div className="text-sm sm:text-lg">{format(date,'d')}</div>
{!isSelected && (hasHolds || hasScheduled) && (
<div className="flex gap-0.5 justify-center mt-0.5 sm:mt-1">
{hasHolds && <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-brand rounded-full" />}
{hasScheduled && <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-ok rounded-full" />}
</div>
)}
</button>
 )
 })}
 </div>
 
 <button
 onClick={() => setSelectedDate(addDays(displayDate, 1))}
 className="p-1.5 sm:p-2 rounded-lg hover:bg-surface-alt flex-shrink-0"
 >
 <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
 </button>
 </div>
 
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
 {/* Left: Items List - Split into On Hold and To Schedule */}
 <div className="space-y-3 sm:space-y-4 max-h-[calc(100vh-200px)] lg:max-h-[calc(100vh-200px)] overflow-y-auto pr-1 sm:pr-2">
 {/* Search */}
 <div className="sticky top-0 bg-surface pb-2 z-10">
 <div className="relative">
 <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
 <input
 type="text"
 placeholder="Search tasks..."
 value={searchQuery}
 onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
 className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-soft 
 bg-surface focus:ring-2 focus:ring-brand"
 />
 </div>
 </div>
 
{/* ═══════════════════════════════════════════════════════════════════════════ */}
{/* SECTION 1: the times Horton is holding.
    Ratified decision #2 fixes the vocabulary at Flexible 🔓 / Confirmed 🔒.
    The subtitle states what Flexible MEANS (Horton may still move it),
    not what the user owes — and nothing here pulses, counts down, or
    marks the section incomplete (SC-6). */}
 {/* ═══════════════════════════════════════════════════════════════════════════ */}
 {itemsOnHold.length > 0 && (
 <div className="space-y-2">
 <h2 className="text-xs sm:text-sm font-semibold text-brand flex items-center gap-1.5 sm:gap-2 sticky top-12 bg-surface py-2 z-10 flex-wrap">
 <div className="p-1 bg-brand-subtle rounded flex-shrink-0">
 <Unlock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
 </div>
Flexible ({itemsOnHold.length})
<span className="text-app-micro sm:text-xs font-normal text-secondary hidden sm:inline">— {BRAND.assistant} can still move these</span>
 </h2>

 <div className="space-y-2">
 {itemsOnHold.map((item) => {
 const hold = getHoldForItem(item)
 const holdTime = hold ? holdStartTime(hold) :''
 const isConfirmedHold = hold?.commitment ==='confirmed'
 const busy = !!hold && holdVerbBusy === hold.id

 return (
 <div
 key={`hold-${item.type}-${item.id}`}
 onClick={() => hold && setSelectedHold(hold)}
 className={`relative p-3 rounded-xl border-2 cursor-pointer hover:shadow-md
 transition-all group ${isConfirmedHold
 ?'border-solid border-ok bg-ok/10'
 :'border-dashed border-brand bg-brand-subtle hover:border-brand-subtle'}`}
 >
 <div className="flex items-start gap-3">
 <div className={`p-1.5 rounded-lg ${isConfirmedHold ?'bg-ok/10 text-ok' :'bg-brand-subtle text-brand'}`}>
 {isConfirmedHold ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
 </div>

<div className="flex-1 min-w-0">
<div className="flex items-center gap-2 flex-wrap">
<span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
isConfirmedHold ?'bg-ok/20 text-ok' :'bg-brand-subtle text-brand'
}`}>
{isConfirmedHold ? COMMITMENT_LABEL.confirmed : COMMITMENT_LABEL.flexible}
</span>
<span className={`w-2 h-2 rounded-full ${priorityBadgeColors[item.priority]}`} />
 </div>

 <h3 className="font-medium text-primary mt-1 truncate">
 {item.title}
 </h3>

 {/* Hold Date/Time */}
 {hold && (
 <div className="mt-2 flex items-center gap-2 text-sm">
 <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-brand-subtle text-brand">
 <Calendar className="w-3.5 h-3.5" />
 <span className="font-medium">{format(parseISO(hold.date_key),'EEE, MMM d')}</span>
 </div>
 <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-brand-subtle text-brand">
 <Clock className="w-3.5 h-3.5" />
 <span className="font-medium">{holdTime}</span>
 </div>
 <span className="text-secondary">{hold.duration_minutes}m</span>
 </div>
 )}

{/* Inline verbs. Confirm is offered, never demanded; "Keep
    flexible" is the equal-weight answer, and on an already-Flexible
    block it writes nothing at all.

    Two labels change with what the block actually is. On a recurring
    hold, Confirm commits the whole rule and the release only takes one
    day off it, so they read "Confirm the whole rhythm" and "Not today"
    (C7/C12). And the flexible-again control is withheld entirely where
    nothing can address the block — an item-scoped write there would
    land on a different item's row (C8/C20). */}
 {hold && (
 <div className="mt-2.5 flex items-center gap-2 flex-wrap">
 {!isConfirmedHold && (
 <button
 onClick={(e) => { e.stopPropagation(); confirmHold(hold) }}
 disabled={busy}
 title={hold.is_recurring_master
 ?'Confirms every time this repeats, not just this day'
 :'Confirm this time'}
 className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-ok/10 text-ok hover:bg-ok/20 disabled:opacity-50"
 >
 {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
 {confirmVerbLabel(hold)}
 </button>
 )}
 {canKeepFlexible(hold) && (
 <button
 onClick={(e) => { e.stopPropagation(); keepHoldFlexible(hold) }}
 disabled={busy}
 className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-surface text-secondary hover:bg-surface-alt disabled:opacity-50"
 >
 <Unlock className="w-3.5 h-3.5" />
 Keep flexible
 </button>
 )}
 <button
 onClick={(e) => { e.stopPropagation(); removeHold(hold) }}
 disabled={busy}
 title={releaseEndsHold(hold)
 ?'Take this off the day'
 :'Takes it off that day. The rhythm keeps going.'}
 className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-muted hover:text-bad hover:bg-bad/10 disabled:opacity-50"
 >
 <X className="w-3.5 h-3.5" />
 {releaseEndsHold(hold) ?'Remove' :'Not today'}
 </button>
 </div>
 )}
 </div>

 <ChevronRight className="w-5 h-5 text-brand group-hover:text-brand transition-colors" />
 </div>
 </div>
 )
 })}
 </div>
 </div>
 )}
 
 {/* ═══════════════════════════════════════════════════════════════════════════ */}
 {/* SECTION 2: Tasks to Schedule (Not yet scheduled) */}
 {/* ═══════════════════════════════════════════════════════════════════════════ */}
 <div className="space-y-2">
 <h2 className="text-xs sm:text-sm font-semibold text-primary flex items-center gap-1.5 sm:gap-2 sticky top-12 bg-surface py-2 z-10 flex-wrap">
 <div className="p-1 bg-surface-alt rounded flex-shrink-0">
 <Target className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-secondary" />
 </div>
 To Schedule ({itemsToSchedule.length})
 <span className="text-app-micro sm:text-xs font-normal text-secondary hidden sm:inline">— drag to calendar or click to quick schedule</span>
 </h2>
 
 {itemsToSchedule.length === 0 ? (
 <div className="p-4 text-center text-secondary bg-surface-alt rounded-lg">
 <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-ok" />
 <p className="font-medium">All caught up!</p>
 <p className="text-sm">No more items to schedule.</p>
 </div>
 ) : (
 <div className="space-y-2">
 {paginatedItems.map((item) => {
 const scheduled = isItemScheduled(item.id)
 const scheduledInfo = suggestions?.scheduled_items.find(s => s.item_id === item.id)
 
 return (
 <div
 key={`${item.type}-${item.id}`}
 draggable={!scheduled}
 onDragStart={(e) => handleDragStart(e, item)}
 onDragEnd={handleDragEnd}
 onClick={() => setSelectedItem(selectedItem?.id === item.id ? null : item)}
className={`
relative p-2.5 sm:p-4 rounded-lg sm:rounded-xl border-l-4 cursor-pointer transition-all
${priorityColors[item.priority] || priorityColors.medium}
${scheduled ?'opacity-60' :'hover:shadow-md'}
${selectedItem?.id === item.id ?'ring-2 ring-brand' :''}
${!scheduled ?'cursor-grab active:cursor-grabbing' :''}
${item.type === 'habit' ?'border-dashed bg-surface-alt/30' :''}
`}
 >
 <div className="flex items-start gap-2 sm:gap-3">
 {/* Drag Handle */}
 {!scheduled && (
 <div className="text-muted mt-0.5">
 <GripVertical className="w-4 h-4" />
 </div>
 )}
 
 <div className="flex-1 min-w-0">
 {/* Type Badge, Icon, and Priority */}
 <div className="flex items-center gap-2 mb-1 flex-wrap">
 {item.type === 'habit' ? (
 <Repeat className="w-4 h-4 text-ok" />
 ) : item.type === 'skill' ? (
 <Brain className="w-4 h-4 text-warn" />
 ) : item.type === 'roadblock' ? (
 <Shield className="w-4 h-4 text-bad" />
 ) : item.type === 'action_item' ? (
 <Zap className="w-4 h-4 text-warn" />
 ) : (
 <Target className="w-4 h-4 text-brand" />
 )}
{item.type !== 'task' && (() => {
  const badge = typeBadgeConfig[item.type]
  return badge ? (
    <span className={`px-1.5 py-0.5 rounded text-app-nano font-semibold uppercase ${badge.bgClass} ${badge.textClass}`}>
      {badge.label}
    </span>
  ) : null
})()}
{item.is_skill_journey_task && (
  <span className="px-1.5 py-0.5 rounded text-app-nano font-semibold uppercase bg-brand-subtle text-brand">
    Skill Step
  </span>
)}
<span className={`w-2 h-2 rounded-full ${priorityBadgeColors[item.priority]}`} />
 <span className="text-xs text-secondary capitalize">{item.priority}</span>
 {item.due_date && (
 <span className="text-xs text-muted">Due: {item.due_date}</span>
 )}
 {/* Scheduling hint indicator */}
 {item.scheduling_hint?.is_recurring && (
 <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-brand-subtle text-brand">
 <Repeat className="w-3 h-3" />
 {item.scheduling_hint.rrule ? (() => {
 const { pattern } = parseRrule(item.scheduling_hint!.rrule)
 return pattern ==='daily' ?'Daily' :
 pattern ==='weekdays' ?'Weekdays' :
 pattern ==='weekly' ?'Weekly' :
 pattern ==='biweekly' ?'Bi-weekly' :
 pattern ==='monthly' ?'Monthly' :
 pattern ==='custom' ?'Custom' :'Recurring'
 })() :'Recurring'}
 </span>
 )}
 {item.scheduling_hint?.suggested_time_of_day && (
 <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-warn/10 text-warn">
 <Clock className="w-3 h-3" />
 {item.scheduling_hint.suggested_time_of_day}
 </span>
 )}
 {/* Recurring indicator for items without scheduling_hint (e.g. habits/skills from hierarchy) */}
 {item.is_recurring && !item.scheduling_hint?.is_recurring && (
 <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-brand-subtle text-brand">
 <Repeat className="w-3 h-3" />
 {item.recurrence_pattern ? (
   item.recurrence_pattern === 'daily' ? 'Daily' :
   item.recurrence_pattern === 'weekdays' ? 'Weekdays' :
   item.recurrence_pattern === 'weekly' ? 'Weekly' :
   item.recurrence_pattern === 'biweekly' ? 'Bi-weekly' :
   item.recurrence_pattern === 'monthly' ? 'Monthly' :
   'Recurring'
 ) : 'Recurring'}
 </span>
 )}
 </div>
 
 {/* Title */}
 <h3 className="font-medium text-primary">
 {item.title}
 </h3>
 
{/* Parent Hierarchy - Skill, Task & Goal */}
{(item.parent_skill_title || item.parent_task_title || item.parent_goal_title) && (
<div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
{item.parent_skill_title && (
<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-subtle text-brand">
<Zap className="w-3 h-3" />
<span className="font-medium">from</span> {item.parent_skill_title}
</span>
)}
{item.parent_goal_title && (
<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-subtle text-brand">
<Target className="w-3 h-3" />
<span className="font-medium">Goal:</span> {item.parent_goal_title}
</span>
)}
{item.parent_task_title && (
<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-subtle text-brand">
<FileText className="w-3 h-3" />
<span className="font-medium">Task:</span> {item.parent_task_title}
</span>
)}
</div>
)}
 
 {/* Description Preview */}
 {item.description && selectedItem?.id === item.id && (
 <p className="mt-2 text-sm text-secondary line-clamp-2">
 {item.description}
 </p>
 )}
 
 {/* Duration */}
 <div className="mt-2 flex items-center gap-4 text-sm text-secondary">
 <span className="flex items-center gap-1">
 <Clock className="w-3.5 h-3.5" />
 {item.estimated_duration}m
 </span>
 
 {scheduled && scheduledInfo && (
 <span className="flex items-center gap-1 text-ok">
 <CheckCircle2 className="w-3.5 h-3.5" />
 {scheduledInfo.suggested_date} @ {scheduledInfo.suggested_time}
 </span>
 )}
 </div>
 </div>
 
 {/* Actions */}
 <div className="flex flex-col gap-1">
 {scheduled ? (
 <button
 onClick={(e) => { e.stopPropagation(); removeScheduledItem(item.id) }}
 className="p-1.5 rounded-lg text-bad hover:bg-bad/10"
 title="Remove from schedule"
 >
 <X className="w-4 h-4" />
 </button>
 ) : (
 <button
 onClick={(e) => { e.stopPropagation(); openQuickSchedule(item) }}
 className="p-1.5 rounded-lg text-brand hover:bg-brand-subtle"
 title="Quick schedule"
 >
 <Calendar className="w-4 h-4" />
 </button>
 )}
 </div>
 </div>
 </div>
 )
 })}
 </div>
 )}
 
 {/* Pagination for Tasks to Schedule */}
 {totalPages > 1 && (
 <div className="flex items-center justify-center gap-2 pt-4">
 <button
 onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
 disabled={currentPage === 1}
 className="p-2 rounded-lg hover:bg-surface-alt disabled:opacity-50"
 >
 <ChevronLeft className="w-4 h-4" />
 </button>
 
 <div className="flex gap-1">
 {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
 let pageNum: number
 if (totalPages <= 5) {
 pageNum = i + 1
 } else if (currentPage <= 3) {
 pageNum = i + 1
 } else if (currentPage >= totalPages - 2) {
 pageNum = totalPages - 4 + i
 } else {
 pageNum = currentPage - 2 + i
 }
 
 return (
 <button
 key={pageNum}
 onClick={() => setCurrentPage(pageNum)}
 className={`
 w-8 h-8 rounded-lg text-sm font-medium
 ${currentPage === pageNum
 ?'bg-brand text-on-brand'
 :'bg-surface hover:bg-surface-alt'
 }
`}
 >
 {pageNum}
 </button>
 )
 })}
 </div>
 
 <button
 onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
 disabled={currentPage === totalPages}
 className="p-2 rounded-lg hover:bg-surface-alt disabled:opacity-50"
 >
 <ChevronRight className="w-4 h-4" />
 </button>
 
 <span className="text-sm text-secondary ml-2">
 Page {currentPage} of {totalPages}
 </span>
 </div>
 )}
 </div>
 </div>
 
 {/* Right: Day Schedule View */}
 <div className="space-y-3 sm:space-y-4">
 <div className="flex items-center justify-between gap-2">
 <h2 className="text-sm sm:text-lg font-semibold text-primary truncate">
 {format(displayDate,'EEE, MMM d')}
 </h2>
 <div className="text-xs sm:text-sm text-secondary flex items-center gap-2 sm:gap-3 flex-shrink-0">
 {holdsForDate.length > 0 && (
 <span className="flex items-center gap-1 text-brand">
 <Unlock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
 {holdsForDate.length}
 <span className="hidden sm:inline"> flexible</span>
 </span>
 )}
 {scheduledItemsForDate.length > 0 && (
 <span className="flex items-center gap-1 text-ok">
 <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
 {scheduledItemsForDate.length}
 <span className="hidden sm:inline"> confirmed</span>
 </span>
 )}
 </div>
 </div>
 
 {/* Timeline */}
 <Card className="p-2 sm:p-4 overflow-hidden">
 <div
 ref={timelineScrollRef}
 className="relative overflow-y-auto"
 style={{ maxHeight:'min(70vh, 720px)' }}
 >
 <div className="relative" style={{ height:`${HOUR_SLOTS.length * 60}px` }}>
 {/* Hour Lines */}
 {HOUR_SLOTS.map((hour) => {
 const isWorkHour = hour >= 9 && hour < 18
 const isFreeSlot = freeSlotsForDate.some(slot => {
 const slotStart = parseInt(slot.start.split(':')[0])
 const slotEnd = parseInt(slot.end.split(':')[0])
 return hour >= slotStart && hour < slotEnd
 })
 
 return (
 <div
 key={hour}
 className={`
 absolute w-full h-15 border-t border-soft
 ${dropTargetHour === hour ?'bg-brand-subtle' :''}
 ${isFreeSlot && !dropTargetHour ?'bg-ok/10' :''}
 ${!isWorkHour ?'bg-surface' :''}
 transition-colors
`}
 style={{ top:`${hour * 60}px` }}
 onDragOver={(e) => handleDragOver(e, hour)}
 onDragLeave={handleDragLeave}
 onDrop={(e) => handleDrop(e, hour)}
 >
 <span className="absolute -left-0.5 sm:-left-1 -top-2.5 text-app-micro sm:text-xs text-muted bg-surface px-0.5 sm:px-1">
 {hour === 0 ?'12a' : hour === 12 ?'12p' : hour > 12 ?`${hour - 12}p` :`${hour}a`}
 </span>
 
 {/* Drop indicator */}
 {dropTargetHour === hour && draggedItem && (
 <div className="absolute inset-x-8 sm:inset-x-12 top-1 h-[calc(100%-8px)] rounded-lg border-2 border-dashed border-brand bg-brand-subtle flex items-center justify-center">
 <span className="text-xs sm:text-sm text-brand font-medium truncate px-1">
 Drop here
 </span>
 </div>
 )}
 </div>
 )
 })}
 
 {/* Calendar Events (existing) — external synced vs confirmed the assistant blocks */}
 {eventsForDate.map((event, i) => {
 if (!event.start) return null
 const endTime = event.end || event.start
 const [startH, startM] = event.start.split(':').map(Number)
 const [endH, endM] = endTime.split(':').map(Number)
 const duration = (endH * 60 + endM) - (startH * 60 + startM)
 const { top, height } = getTimelinePosition(event.start, duration || 60)
 const isExternal = !!event.external_system
 
 return (
 <div
 key={`event-${i}`}
 className={`absolute left-8 sm:left-12 right-1 sm:right-2 rounded-md sm:rounded-lg border p-1 sm:p-2 overflow-hidden ${
 isExternal
 ?'bg-info/10 border-info'
 :'bg-ok/10 border-ok'
 }`}
 style={{ top, height }}
 >
 <div className="flex items-center gap-1">
 {isExternal ? (
 <Globe className="w-3 h-3 text-info flex-shrink-0" />
 ) : (
 <Lock className="w-3 h-3 text-ok flex-shrink-0" />
 )}
 <span className={`text-xs font-medium truncate ${
 isExternal
 ?'text-info'
 :'text-ok'
 }`}>
 {event.title}
 </span>
 {isExternal && (
 <span className="text-app-micro px-1 py-0.5 rounded bg-info/20 text-info flex-shrink-0">
 SYNCED
 </span>
 )}
 </div>
 <div className={`text-xs ${
 isExternal
 ?'text-info'
 :'text-ok'
 }`}>
 {event.start} - {event.end}
 </div>
 </div>
 )
 })}
 
{/* Flexible blocks (dashed border, 🔓) vs Confirmed (solid, 🔒) —
    the SC-14 visual distinction on this page's timeline. No pulse: a
    Flexible block is a finished, usable state, not a pending item. */}
 {holdsForDate.map((hold) => {
 const startTime = holdStartTime(hold)
 const { top, height } = getTimelinePosition(startTime, hold.duration_minutes)
 const isConfirmedHold = hold.commitment ==='confirmed'

 return (
 <div
 key={`hold-${hold.id}`}
 className={`
 absolute left-8 sm:left-12 right-1 sm:right-2 rounded-md sm:rounded-lg p-1.5 sm:p-2 overflow-hidden cursor-pointer group
 transition-all hover:shadow-lg hover:scale-[1.02]
 ${isConfirmedHold
 ?'bg-ok/10 border-2 border-solid border-ok'
 :'bg-brand-subtle border-2 border-dashed border-brand'}
`}
 style={{ top, height }}
 onClick={() => setSelectedHold(hold)}
 title={isConfirmedHold ?'Confirmed — click for options' :`Flexible — ${BRAND.assistant} can still move this`}
 >
 <div className="flex items-start justify-between gap-2">
 <div className={`flex-shrink-0 p-1.5 rounded-lg shadow-sm ${isConfirmedHold ?'bg-ok/10 text-ok' :'bg-brand-subtle text-brand'}`}>
 {isConfirmedHold ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
 </div>

 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-1">
 <Clock className={`w-3 h-3 flex-shrink-0 ${isConfirmedHold ?'text-ok' :'text-brand'}`} />
 <span className={`text-xs font-semibold truncate ${isConfirmedHold ?'text-ok' :'text-brand'}`}>
 {hold.title}
 </span>
<span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
isConfirmedHold ?'bg-ok/20 text-ok' :'bg-brand-subtle text-brand'
}`}>
{isConfirmedHold ? COMMITMENT_LABEL.confirmed : COMMITMENT_LABEL.flexible}
</span>
 </div>
 {height > 40 && (
 <div className={`text-xs mt-0.5 ${isConfirmedHold ?'text-ok' :'text-brand'}`}>
 {startTime} • {hold.duration_minutes}m
 </div>
 )}
 </div>

 {/* Quick actions - visible on hover */}
 <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
 {/* Quick remove button */}
 <button
 onClick={(e) => {
 e.stopPropagation()
 removeHold(hold)
 }}
 className="p-1 rounded bg-bad/10 text-bad hover:bg-bad"
 title={releaseEndsHold(hold)
 ?'Take this off the day'
 :'Not today. The rhythm keeps going.'}
 >
 <X className="w-3 h-3" />
 </button>
 {/* Chevron to open details */}
 <div className="text-brand">
 <ChevronRight className="w-4 h-4" />
 </div>
 </div>
 </div>
 </div>
 )
 })}
 
 {/* Scheduled Items - with Lock/Unlock status indicator */}
 {scheduledItemsForDate.map((item) => {
 const { top, height } = getTimelinePosition(item.suggested_time, item.duration_minutes)
 const originalItem = items.find(i => i.id === item.item_id)
// Rows in `scheduled_items` are placements the USER made by hand (drag
// or quick-schedule), so an unstamped one is Confirmed by default —
// SC-7, and the reason Horton's own placements never come through here.
 const isConfirmed = item.block_status ==='confirmed' || !item.block_status
 
 return (
 <div
 key={`scheduled-${item.item_id}`}
 className={`
 absolute left-8 sm:left-12 right-1 sm:right-2 rounded-md sm:rounded-lg p-1.5 sm:p-2 overflow-hidden cursor-pointer
 transition-all hover:shadow-lg
 ${isConfirmed
 ? (item.item_type === 'roadblock'
 ? 'bg-bad/10 border-2 border-solid border-bad'
 : item.item_type === 'skill'
 ? 'bg-warn/10 border-2 border-solid border-warn'
 : item.item_type === 'habit'
 ? 'bg-ok/10 border-2 border-solid border-ok'
 : 'bg-ok/10 border-2 border-solid border-ok')
 :'bg-brand-subtle border-2 border-dashed border-brand'
 }
`}
 style={{ top, height }}
 onClick={() => originalItem && setSelectedItem(originalItem)}
 >
 <div className="flex items-start justify-between gap-2">
 {/* Lock/Unlock Toggle Button - More prominent */}
 <button
 onClick={(e) => {
 e.stopPropagation();
 toggleItemLockStatus(item.item_id, item.item_type, isConfirmed ?'confirmed' :'proposed');
 }}
 className={`flex-shrink-0 p-1.5 rounded-lg transition-all shadow-sm ${
 isConfirmed
 ?'bg-ok/10 text-ok hover:bg-brand-subtle hover:text-brand'
 :'bg-brand-subtle text-brand hover:bg-ok/10 hover:text-ok'
 }`}
 title={isConfirmed
 ? `Confirmed — click to hand it back to ${BRAND.assistant}`
 : 'Flexible — click to confirm this time'}
 >
 {isConfirmed ? (
 <Lock className="w-4 h-4" />
 ) : (
 <Unlock className="w-4 h-4" />
 )}
 </button>
 
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-1">
 {item.item_type === 'habit' ? (
 <Repeat className={`w-3 h-3 flex-shrink-0 ${isConfirmed ? 'text-ok' : 'text-brand'}`} />
 ) : item.item_type === 'skill' ? (
 <Brain className={`w-3 h-3 flex-shrink-0 ${isConfirmed ? 'text-warn' : 'text-brand'}`} />
 ) : item.item_type === 'roadblock' ? (
 <Shield className={`w-3 h-3 flex-shrink-0 ${isConfirmed ? 'text-bad' : 'text-brand'}`} />
 ) : item.item_type === 'action_item' ? (
 <Zap className={`w-3 h-3 flex-shrink-0 ${isConfirmed ? 'text-warn' : 'text-brand'}`} />
 ) : (
 <Target className={`w-3 h-3 flex-shrink-0 ${isConfirmed ? 'text-ok' : 'text-brand'}`} />
 )}
 <span className="text-xs font-medium text-primary truncate">
 {item.title}
 </span>
{/* Same two words everywhere (decision #2 / SC-36). */}
<span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
isConfirmed ?'bg-ok/20 text-ok' :'bg-brand-subtle text-brand'
}`}>
{isConfirmed ? COMMITMENT_LABEL.confirmed : COMMITMENT_LABEL.flexible}
</span>
 </div>
 {height > 40 && (
 <div className="text-xs text-secondary mt-0.5">
 {item.suggested_time} • {item.duration_minutes}m
 </div>
 )}
 </div>
 
 <button
 onClick={(e) => { e.stopPropagation(); removeScheduledItem(item.item_id) }}
 className="flex-shrink-0 p-1 rounded text-muted hover:text-bad hover:bg-bad/10"
 title="Remove from schedule"
 >
 <X className="w-3.5 h-3.5" />
 </button>
 </div>
 </div>
 )
 })}
 </div>
 </div>
 </Card>
 
{/* Legend for the two block styles. Under HOLD-first the dashed vs
    solid distinction carries real meaning (SC-14 requires it be
    visible), so it is worth the three lines it costs. It states what
    each state IS — never how many of either the user has, and never
    that one of them is unfinished. */}
 {(holdsForDate.length > 0 || scheduledItemsForDate.length > 0) && (
 <div className="flex items-center gap-4 text-xs text-secondary px-1">
 <span className="flex items-center gap-1.5">
 <span className="w-3 h-3 rounded border-2 border-dashed border-brand bg-brand-subtle" />
 <Unlock className="w-3 h-3" />
 {COMMITMENT_LABEL.flexible} — {BRAND.assistant} may move it
 </span>
 <span className="flex items-center gap-1.5">
 <span className="w-3 h-3 rounded border-2 border-solid border-ok bg-ok/10" />
 <Lock className="w-3 h-3" />
 {COMMITMENT_LABEL.confirmed} — yours
 </span>
 </div>
 )}
 </div>
 </div>
 </div>
 
 {/* Enhanced Quick Schedule Modal */}
 {quickScheduleItem && (
 <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
 <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setQuickScheduleItem(null)} />
 
 <div className="relative bg-surface rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
 {/* Header */}
 <div className="sticky top-0 bg-surface border-b border-soft px-3 sm:px-6 py-3 sm:py-4">
 <div className="flex items-start justify-between">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-1">
 {(() => {
   const badge = typeBadgeConfig[quickScheduleItem.type]
   const Icon = quickScheduleItem.type === 'habit' ? Repeat
     : quickScheduleItem.type === 'skill' ? Brain
     : quickScheduleItem.type === 'roadblock' ? Shield
     : quickScheduleItem.type === 'action_item' ? Zap
     : Target
   return badge ? (
     <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bgClass} ${badge.textClass}`}>
       <Icon className="w-3 h-3" /> {badge.label}
     </span>
   ) : null
 })()}
 <span className={`w-2 h-2 rounded-full ${priorityBadgeColors[quickScheduleItem.priority]}`} />
 <span className="text-xs text-secondary capitalize">{quickScheduleItem.priority}</span>
 </div>
 <h2 className="text-lg font-semibold text-primary">
 {quickScheduleItem.title}
 </h2>
 
{/* Parent Hierarchy - Skill, Goal & Task */}
{(quickScheduleItem.parent_skill_title || quickScheduleItem.parent_goal_title || quickScheduleItem.parent_task_title) && (
<div className="mt-2 flex flex-wrap gap-2">
{quickScheduleItem.parent_skill_title && (
<span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-brand-subtle text-brand">
<Zap className="w-3 h-3" />
<span className="font-medium">from</span> {quickScheduleItem.parent_skill_title}
</span>
)}
{quickScheduleItem.parent_goal_title && (
<span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-brand-subtle text-brand">
<Target className="w-3 h-3" />
<span className="font-medium">Goal:</span> {quickScheduleItem.parent_goal_title}
</span>
)}
{quickScheduleItem.parent_task_title && (
<span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-brand-subtle text-brand">
<FileText className="w-3 h-3" />
<span className="font-medium">Task:</span> {quickScheduleItem.parent_task_title}
</span>
)}
 </div>
 )}
 
 {quickScheduleItem.description && (
 <p className="mt-2 text-sm text-secondary line-clamp-2">
 {quickScheduleItem.description}
 </p>
 )}
 
 {/* Scheduling hint from AI */}
 {quickScheduleItem.scheduling_hint && (
 <div className="mt-2 p-2 rounded-lg bg-brand-subtle border border-brand-subtle">
 <div className="flex items-start gap-2">
 <Sparkles className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
 <div className="text-xs">
 <span className="font-medium text-brand">AI Scheduling Hint: </span>
 <span className="text-brand">
 {quickScheduleItem.scheduling_hint.rationale || 
 (quickScheduleItem.scheduling_hint.is_recurring 
 ?`Suggested as ${(() => {
 const { pattern } = parseRrule(quickScheduleItem.scheduling_hint?.rrule || null)
 return pattern ||'recurring'
 })()} activity`
 :'One-time activity')}
 {quickScheduleItem.scheduling_hint.suggested_time_of_day && 
` • Best in the ${quickScheduleItem.scheduling_hint.suggested_time_of_day}`}
 </span>
 </div>
 </div>
 </div>
 )}
 </div>
 <button
 onClick={() => setQuickScheduleItem(null)}
 className="p-2 rounded-lg hover:bg-surface-alt text-secondary"
 >
 <X className="w-5 h-5" />
 </button>
 </div>
 </div>
 
 {/* Form Content */}
 <div className="p-3 sm:p-6 space-y-4 sm:space-y-5">
 {/* Date & Time Row */}
 <div className="grid grid-cols-2 gap-2 sm:gap-4">
 <div>
 <label className="block text-sm font-medium text-primary mb-1.5">
 <Calendar className="w-4 h-4 inline mr-1" /> Date
 </label>
 <input
 type="date"
 value={quickScheduleDate}
 onChange={(e) => setQuickScheduleDate(e.target.value)}
 className="w-full px-3 py-2.5 rounded-xl border border-soft 
 bg-surface text-primary
 focus:ring-2 focus:ring-brand focus:border-transparent"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-primary mb-1.5">
 <Clock className="w-4 h-4 inline mr-1" /> Start Time
 </label>
 <input
 type="time"
 value={quickScheduleTime}
 onChange={(e) => setQuickScheduleTime(e.target.value)}
 className="w-full px-3 py-2.5 rounded-xl border border-soft 
 bg-surface text-primary
 focus:ring-2 focus:ring-brand focus:border-transparent"
 />
 </div>
 </div>
 
 {/* Duration */}
 <div>
 <label className="block text-sm font-medium text-primary mb-1.5">
 Duration
 </label>
 <div className="flex flex-wrap gap-2">
 {[15, 30, 45, 60, 90, 120].map((d) => (
 <button
 key={d}
 onClick={() => setQuickScheduleDuration(d)}
 className={`
 px-4 py-2 rounded-xl text-sm font-medium transition-all
 ${quickScheduleDuration === d
 ?'bg-brand text-on-brand shadow-md'
 :'bg-surface-alt text-primary hover:bg-surface-alt'
 }
`}
 >
 {d}m
 </button>
 ))}
 </div>
 <div className="mt-2 flex items-center gap-2">
 <input
 type="number"
 value={quickScheduleDuration}
 onChange={(e) => setQuickScheduleDuration(parseInt(e.target.value) || 30)}
 min="5"
 max="480"
 step="5"
 className="w-20 px-3 py-1.5 rounded-lg border border-soft 
 bg-surface text-primary text-center text-sm"
 />
 <span className="text-sm text-secondary">minutes</span>
 <span className="ml-auto text-sm text-secondary">
 Ends at: <span className="font-medium text-primary">{calculateEndTime(quickScheduleTime, quickScheduleDuration)}</span>
 </span>
 </div>
 </div>
 
 {/* Recurrence */}
 <div>
 <label className="block text-sm font-medium text-primary mb-1.5">
 <Repeat className="w-4 h-4 inline mr-1" /> Recurrence
 </label>
 <div className="grid grid-cols-2 gap-2">
 {RECURRENCE_PATTERNS.map((p) => (
 <button
 key={p.label}
 onClick={() => {
 setQuickScheduleRecurrence(p.value)
 if (p.value !=='custom') setQuickScheduleCustomDays([])
 }}
 className={`
 px-3 py-2 rounded-xl text-sm font-medium transition-all text-left
 ${quickScheduleRecurrence === p.value || (!quickScheduleRecurrence && !p.value)
 ?'bg-brand text-on-brand shadow-md'
 :'bg-surface-alt text-primary hover:bg-surface-alt'
 }
`}
 >
 {p.label}
 </button>
 ))}
 </div>
 
 {/* Custom Days Selection */}
 {quickScheduleRecurrence ==='custom' && (
 <div className="mt-3 p-4 bg-surface rounded-xl">
 <label className="block text-sm font-medium text-secondary mb-2">
 Select Days
 </label>
 <div className="flex gap-2 justify-center">
 {DAYS_OF_WEEK.map((day) => (
 <button
 key={day.value}
 onClick={() => toggleCustomDay(day.value)}
 className={`
 w-10 h-10 rounded-full text-sm font-medium transition-all
 ${quickScheduleCustomDays.includes(day.value)
 ?'bg-brand text-on-brand shadow-md'
 :'bg-surface text-primary hover:bg-surface-alt border border-soft'
 }
`}
 title={day.full}
 >
 {day.label}
 </button>
 ))}
 </div>
 </div>
 )}
 
 {/* Recurrence End Date */}
 {quickScheduleRecurrence && (
 <div className="mt-3 p-4 bg-surface rounded-xl">
 <label className="block text-sm font-medium text-secondary mb-1.5">
 End Date (Optional)
 </label>
 <input
 type="date"
 value={quickScheduleEndDate}
 onChange={(e) => setQuickScheduleEndDate(e.target.value)}
 min={quickScheduleDate}
 className="w-full px-3 py-2 rounded-xl border border-soft 
 bg-surface text-primary"
 />
 <p className="mt-1 text-xs text-secondary">Leave empty for indefinite recurrence</p>
 </div>
 )}
 </div>
 
 {/* Summary */}
 <div className="p-4 bg-brand rounded-xl border border-brand-subtle">
 <h4 className="text-sm font-semibold text-brand mb-2">📋 Summary</h4>
 <div className="space-y-1 text-sm text-brand">
 <p>
 <span className="font-medium">📅 Date:</span> {format(parseISO(quickScheduleDate),'EEEE, MMMM d, yyyy')}
 </p>
 <p>
 <span className="font-medium">⏰ Time:</span> {quickScheduleTime} - {calculateEndTime(quickScheduleTime, quickScheduleDuration)} ({quickScheduleDuration}m)
 </p>
 {quickScheduleRecurrence && (
 <p>
 <span className="font-medium">🔄 Repeats:</span>{''}
 {quickScheduleRecurrence ==='daily' &&'Every day'}
 {quickScheduleRecurrence ==='weekdays' &&'Every weekday (Mon-Fri)'}
 {quickScheduleRecurrence ==='weekly' &&'Every week'}
 {quickScheduleRecurrence ==='biweekly' &&'Every 2 weeks'}
 {quickScheduleRecurrence ==='monthly' &&'Every month'}
 {quickScheduleRecurrence ==='custom' && (
 <>On {quickScheduleCustomDays.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.full).filter(Boolean).join(',') ||'selected days'}</>
 )}
 {quickScheduleEndDate && <> until {format(parseISO(quickScheduleEndDate),'MMM d, yyyy')}</>}
 </p>
 )}
 </div>
 </div>
 </div>
 
 {/* Footer */}
 <div className="sticky bottom-0 bg-surface border-t border-soft px-3 sm:px-6 py-3 sm:py-4 flex gap-2 sm:gap-3">
 <Button variant="outline" onClick={() => setQuickScheduleItem(null)} className="flex-1 text-sm">
 Cancel
 </Button>
 <Button onClick={confirmQuickSchedule} className="flex-1 bg-brand text-sm">
 <Check className="w-4 h-4 mr-1" />
 Schedule
 </Button>
 </div>
 </div>
 </div>
 )}
 
{/* AI Assist mode selection modal was removed (2026-04). The AI button
    on the header now calls aiScheduleRemaining() directly in confirm
    mode. See handleAiAssistClick. */}

{/* ═══════════════════════════════════════════════════════════════════════════ */}
{/* REBALANCE MODAL - Two-step: Options → Preview → Confirm */}
 {/* ═══════════════════════════════════════════════════════════════════════════ */}
 {showRebalanceModal && (
 <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
 <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowRebalanceModal(false); setRebalanceStep('options'); setRebalancePreview([]); }} />
 
 <div className="relative bg-surface rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
 {/* Header */}
 <div className="bg-warn px-4 sm:px-6 py-4 sm:py-5">
 <div className="flex items-center gap-2 sm:gap-3">
 <div className="p-1.5 sm:p-2 bg-on-brand/20 rounded-lg sm:rounded-xl flex-shrink-0">
 {rebalanceStep ==='options' ? (
 <RotateCw className="w-5 h-5 sm:w-6 sm:h-6 text-on-brand" />
 ) : (
 <Unlock className="w-5 h-5 sm:w-6 sm:h-6 text-on-brand" />
 )}
 </div>
 <div className="min-w-0">
 <h2 className="text-base sm:text-xl font-bold text-on-brand">
 {rebalanceStep ==='options' ?'Rebalance Schedule' :'Review'}
 </h2>
 <p className="text-warn text-xs sm:text-sm truncate">
 {rebalanceStep ==='options'
 ?`Hand confirmed blocks back to ${BRAND.assistant}`
 :`${rebalancePreview.length} items become flexible`
 }
 </p>
 </div>
 </div>
 <button
 onClick={() => { setShowRebalanceModal(false); setRebalanceStep('options'); setRebalancePreview([]); }}
 className="absolute top-4 right-4 text-on-brand/80 hover:text-on-brand"
 >
 <X className="w-5 h-5" />
 </button>
 </div>
 
 {/* Content - Step 1: Options */}
 {rebalanceStep ==='options' && (
 <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
 <p className="text-secondary">
 This makes your <strong className="text-ok">🔒 Confirmed</strong> blocks
 <strong className="text-brand"> 🔓 Flexible</strong> again, so {BRAND.assistant} can re-optimize
 your workload around them.
 </p>
 
 {/* Scope Selection */}
 <div className="space-y-3">
 <label className="block text-sm font-medium text-primary">
 What should be rebalanced?
 </label>
 
 <label className="flex items-center gap-3 p-3 rounded-lg border border-soft cursor-pointer hover:bg-surface">
 <input
 type="radio"
 name="rebalanceScope"
 value="future"
 checked={rebalanceScope ==='future'}
 onChange={() => setRebalanceScope('future')}
 className="w-4 h-4 text-brand"
 />
 <div>
 <span className="font-medium text-primary">Future only</span>
 <p className="text-sm text-secondary">Rebalance tomorrow and beyond</p>
 </div>
 </label>
 
 <label className="flex items-center gap-3 p-3 rounded-lg border border-soft cursor-pointer hover:bg-surface">
 <input
 type="radio"
 name="rebalanceScope"
 value="all"
 checked={rebalanceScope ==='all'}
 onChange={() => setRebalanceScope('all')}
 className="w-4 h-4 text-brand"
 />
 <div>
 <span className="font-medium text-primary">Everything</span>
 <p className="text-sm text-secondary">Rebalance next 30 days</p>
 </div>
 </label>
 </div>
 
 {/* Include Today Option */}
 <label className="flex items-center gap-3 p-3 rounded-lg border border-warn bg-warn/10 cursor-pointer">
 <input
 type="checkbox"
 checked={rebalanceIncludeToday}
 onChange={(e) => setRebalanceIncludeToday(e.target.checked)}
 className="w-4 h-4 text-warn rounded"
 />
 <div>
 <span className="font-medium text-warn">Include today</span>
 <p className="text-sm text-warn">
 ⚠️ This may disrupt your current day's plan
 </p>
 </div>
 </label>
 
 {/* Note */}
 <div className="p-3 bg-surface rounded-lg text-sm text-secondary">
 <strong>Note:</strong> Blocks you placed or confirmed yourself (🔒) stay put.
 Only placements {BRAND.assistant} made itself become flexible again.
 </div>
 </div>
 )}
 
 {/* Content - Step 2: Preview */}
 {rebalanceStep ==='preview' && (
 <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
 {rebalancePreview.length === 0 ? (
 <div className="text-center py-8">
 <Lock className="w-12 h-12 text-muted mx-auto mb-3" />
 <p className="text-secondary">
 No confirmed blocks found to rebalance in the selected range.
 </p>
 <p className="text-sm text-secondary mt-2">
 Everything here is either yours to keep or already flexible.
 </p>
 </div>
 ) : (
 <>
 <div className="flex items-center gap-2 text-sm text-warn bg-warn/10 p-3 rounded-lg">
 <AlertTriangle className="w-4 h-4" />
 <span>The following <strong>{rebalancePreview.length}</strong> items become flexible:</span>
 </div>
 
 <div className="max-h-64 overflow-y-auto space-y-2 border border-soft rounded-lg p-2">
 {rebalancePreview.map((item) => (
 <div 
 key={item.id}
 className="flex items-center gap-3 p-3 bg-surface rounded-lg"
 >
 <div className="flex items-center gap-2 text-warn">
 <Lock className="w-4 h-4" />
 <ArrowRight className="w-3 h-3" />
 <Unlock className="w-4 h-4 text-brand" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="font-medium text-primary truncate">
 {item.title}
 </p>
 <p className="text-xs text-secondary">
 {item.date} • {item.time_slot}
 </p>
 </div>
 </div>
 ))}
 </div>
 
 <div className="p-3 bg-brand-subtle rounded-lg text-sm text-brand">
 <strong>Afterwards:</strong> {BRAND.assistant} can re-optimize these times.
 Confirm any of them again with the 🔓 icon.
 </div>
 </>
 )}
 </div>
 )}
 
 {/* Footer */}
 <div className="border-t border-soft px-3 sm:px-6 py-3 sm:py-4 flex gap-2 sm:gap-3">
 {rebalanceStep ==='options' ? (
 <>
 <Button variant="outline" onClick={() => { setShowRebalanceModal(false); setRebalanceStep('options'); }} className="flex-1 text-sm">
 Cancel
 </Button>
 <Button 
 onClick={fetchRebalancePreview}
 disabled={loadingPreview}
 className="flex-1 bg-warn text-sm"
 >
 {loadingPreview ? (
 <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Loading...</>
 ) : (
 <>Preview</>
 )}
 </Button>
 </>
 ) : (
 <>
 <Button variant="outline" onClick={() => setRebalanceStep('options')} className="flex-1 text-sm">
 <ChevronLeft className="w-4 h-4 mr-1" />
 Back
 </Button>
 <Button 
 onClick={handleRebalance}
 disabled={rebalancing || rebalancePreview.length === 0}
 className="flex-1 bg-warn text-sm"
 >
 {rebalancing ? (
 <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Working...</>
 ) : (
 <><Unlock className="w-4 h-4 mr-1" />Make flexible ({rebalancePreview.length})</>
 )}
 </Button>
 </>
 )}
 </div>
 </div>
 </div>
 )}
 
 {/* ═══════════════════════════════════════════════════════════════════════════ */}
 {/* BLOCK SHEET — the verbs for one Flexible / Confirmed block.
     Opened on demand only. It is NOT shown on load, and closing it with
     no choice made is a complete, valid outcome (SC-6). */}
 {/* ═══════════════════════════════════════════════════════════════════════════ */}
 {selectedHold && (
 <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
 <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedHold(null)} />

 <div className="relative bg-surface rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
 {/* Simple Header */}
 <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-soft">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 {selectedHold.commitment ==='confirmed' ? (
 <Lock className="w-5 h-5 text-ok" />
 ) : (
 <Unlock className="w-5 h-5 text-brand" />
 )}
 <span className="font-medium text-primary">
 {selectedHold.commitment ==='confirmed' ? COMMITMENT_LABEL.confirmed : COMMITMENT_LABEL.flexible}
 </span>
 </div>
 <button onClick={() => setSelectedHold(null)} className="text-muted hover:text-secondary">
 <X className="w-5 h-5" />
 </button>
 </div>
 </div>

 {/* Content - Clean and Simple */}
 <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
 {/* Task Name - Big and Clear */}
 <h3 className="text-lg font-semibold text-primary">
 {selectedHold.title}
 </h3>
 
 {/* When - Simple display */}
 <div className="flex items-center gap-4 text-sm">
 <div className="flex items-center gap-2 text-secondary">
 <Calendar className="w-4 h-4" />
 <span>{format(parseISO(selectedHold.date_key),'EEE, MMM d')}</span>
 </div>
 <div className="flex items-center gap-2 text-secondary">
 <Clock className="w-4 h-4" />
 <span>
 {holdStartTime(selectedHold)}
 {' →'}
 {holdEndTime(selectedHold)}
 </span>
 </div>
 </div>

{/* What Flexible means, stated once, as a capability rather than a
    request. This replaces the old `let $the assistant reschedule it` opt-in
    checkbox: under decision #1 that IS what Flexible means, so asking
    for it again was friction with no decision behind it. */}
 <p className="text-sm text-secondary">
 {selectedHold.commitment ==='confirmed'
 ?`This time is yours. ${BRAND.assistant} will plan around it.`
 :`${BRAND.assistant} is holding this time and may move it as the week changes. Nothing to do unless you want it settled.`}
 </p>

{/* What the verbs below actually reach, said before they are used.
    On a rhythm the id addresses the RULE: Confirm commits every
    occurrence and the release takes one day off it (C7/C12). */}
 {selectedHold.is_recurring_master && (
 <p className="text-xs text-secondary flex items-start gap-1.5">
 <Repeat className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
 <span>This one repeats. Confirming settles the whole rhythm; taking it off a day leaves the rest running.</span>
 </p>
 )}

{/* Move — offered only for blocks this page can address by their own
    calendar row. /create-blocks returns no row id, so an AI-placed hold
    cannot be re-placed from here without risking a duplicate on the old
    day; withholding the control beats shipping one that silently
    double-books. */}
 {selectedHold.block_id != null && (
 <div className="p-3 rounded-lg border border-soft space-y-2">
 <span className="text-xs font-medium text-primary">Move it</span>
 {/* The PUT this reaches addresses the same row Confirm does, so on a
     rhythm it re-times the whole rule rather than one day. Said out
     loud until a per-occurrence address exists (see followups). */}
 {!releaseEndsHold(selectedHold) && (
 <p className="text-xs text-secondary">Moves every time this repeats.</p>
 )}
 <div className="flex gap-2">
 <input
 type="date"
 value={moveDate || selectedHold.date_key}
 onChange={(e) => setMoveDate(e.target.value)}
 className="flex-1 px-2 py-1.5 text-sm rounded-lg border border-soft bg-surface"
 />
 <input
 type="time"
 value={moveTime || holdStartTime(selectedHold)}
 onChange={(e) => setMoveTime(e.target.value)}
 className="px-2 py-1.5 text-sm rounded-lg border border-soft bg-surface"
 />
 </div>
 {/* SC-7: choosing a time confirms the block by default; this is
     the explicit opt-out that keeps it Horton's to optimize. */}
 <label className="flex items-center gap-2 text-xs text-secondary cursor-pointer">
 <input
 type="checkbox"
 checked={moveKeepFlexible}
 onChange={(e) => setMoveKeepFlexible(e.target.checked)}
 className="w-3.5 h-3.5 rounded border-soft"
 />
 Keep it flexible after moving
 </label>
 <Button
 variant="outline"
 className="w-full text-sm"
 disabled={holdVerbBusy === selectedHold.id}
 onClick={() => moveHold(
 selectedHold,
 moveDate || selectedHold.date_key,
 moveTime || holdStartTime(selectedHold),
 moveKeepFlexible,
 )}
 >
 {holdVerbBusy === selectedHold.id ? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ) : (
 <><ArrowRight className="w-4 h-4 mr-2" />Move</>
 )}
 </Button>
 </div>
 )}
 </div>

{/* Actions. "Keep flexible" is the primary: doing nothing is the
    expected, supported outcome, and Confirm is the exception the user
    reaches for when a time genuinely must not move.

    "Make it flexible again" appears only where a write can actually
    reach this block. Item-scoped, it resolves through parent_task_id
    and on a habit/skill/roadblock would flip whichever task shares the
    number — so it is withheld there rather than aimed blind (C8/C20). */}
 <div className="px-4 sm:px-5 py-3 sm:py-4 bg-surface border-t border-soft">
 <div className="flex gap-2 sm:gap-3">
 {selectedHold.commitment ==='flexible' ? (
 <>
 <Button
 onClick={async () => {
 await confirmHold(selectedHold)
 setSelectedHold(null)
 }}
 disabled={holdVerbBusy === selectedHold.id || confirmingHolds}
 variant="outline"
 className="flex-1 border-soft hover:border-ok hover:bg-ok/10"
 >
 {holdVerbBusy === selectedHold.id || confirmingHolds ? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ) : (
 <><Lock className="w-4 h-4 mr-2" />{confirmVerbLabel(selectedHold)}</>
 )}
 </Button>

 <Button
 onClick={() => { keepHoldFlexible(selectedHold); setSelectedHold(null) }}
 className="flex-1 bg-brand hover:bg-brand-hover text-on-brand"
 >
 <Unlock className="w-4 h-4 mr-2" />
 Keep flexible
 </Button>
 </>
 ) : canKeepFlexible(selectedHold) ? (
 <Button
 onClick={() => { keepHoldFlexible(selectedHold); setSelectedHold(null) }}
 disabled={holdVerbBusy === selectedHold.id}
 variant="outline"
 className="flex-1 border-soft"
 >
 <Unlock className="w-4 h-4 mr-2" />
 Make it flexible again
 </Button>
 ) : (
 <p className="flex-1 text-xs text-secondary">
 This time is yours. To hand it back to {BRAND.assistant}, change it on the item itself.
 </p>
 )}
 </div>

 {/* Remove - very subtle */}
 <button
 onClick={() => {
 removeHold(selectedHold)
 setSelectedHold(null)
 }}
 className="w-full mt-3 text-xs text-muted hover:text-bad transition-colors"
 >
 {releaseEndsHold(selectedHold) ?'Remove from this day' :'Not today'}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 )
}
